import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '../components/ui/alert-dialog';
import { Users, TrendingUp, DollarSign, Search, Download, Eye, Trash2, Ban, CheckCircle, X, Loader2, ShieldAlert, Crown, Snowflake, Briefcase, Plus, Pencil, Clock, Shield, Building2, FileText, Ticket, Percent, Calendar, Bell, Send, RefreshCw, Mail, MessageCircle, AlertTriangle, Activity, Play, CheckCircle2, XCircle, Gift, LogOut } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ==================== COMPONENTE COLABORADORES ====================
const ColaboradoresTab = ({ API }) => {
  const [colaboradores, setColaboradores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nome: '', email: '', senha: '', permissoes: ['users', 'lembretes'] });
  const [saving, setSaving] = useState(false);

  const adminToken = localStorage.getItem('admin_token');
  const config = { headers: { Authorization: `Bearer ${adminToken}` } };

  const fetchColaboradores = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/colaboradores`, config);
      setColaboradores(res.data || []);
    } catch (e) {
      toast.error('Erro ao carregar colaboradores');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchColaboradores(); }, []);

  const openModal = (col = null) => {
    setEditing(col);
    setForm(col ? {
      nome: col.nome,
      email: col.email,
      senha: '',
      permissoes: JSON.parse(col.permissoes || '["users","lembretes"]')
    } : { nome: '', email: '', senha: '', permissoes: ['users', 'lembretes'] });
    setShowModal(true);
  };

  const togglePermissao = (aba) => {
    setForm(f => ({
      ...f,
      permissoes: f.permissoes.includes(aba)
        ? f.permissoes.filter(p => p !== aba)
        : [...f.permissoes, aba]
    }));
  };

  const handleSave = async () => {
    if (!form.nome || !form.email) return toast.error('Nome e email são obrigatórios');
    if (!editing && !form.senha) return toast.error('Senha é obrigatória');
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/admin/colaboradores/${editing.id}`, form, config);
        toast.success('Colaborador atualizado!');
      } else {
        await axios.post(`${API}/admin/colaboradores`, form, config);
        toast.success('Colaborador criado!');
      }
      setShowModal(false);
      fetchColaboradores();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleAtivo = async (col) => {
    try {
      await axios.put(`${API}/admin/colaboradores/${col.id}`, { ativo: col.ativo ? 0 : 1 }, config);
      toast.success(col.ativo ? 'Colaborador desativado' : 'Colaborador ativado');
      fetchColaboradores();
    } catch (e) {
      toast.error('Erro ao atualizar');
    }
  };

  const handleDelete = async (col) => {
    if (!window.confirm(`Remover ${col.nome}?`)) return;
    try {
      await axios.delete(`${API}/admin/colaboradores/${col.id}`, config);
      toast.success('Colaborador removido');
      fetchColaboradores();
    } catch (e) {
      toast.error('Erro ao remover');
    }
  };

  const ABAS_DISPONIVEIS = [
    { id: 'users', label: 'Usuários' },
    { id: 'lembretes', label: 'Lembretes' },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Colaboradores</h2>
          <p className="text-gray-500 mt-1">Gerencie acessos ao backoffice com permissões limitadas</p>
        </div>
        <button
          onClick={() => openModal()}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-white text-sm font-medium hover:bg-primary-hover transition-colors"
        >
          <Plus className="w-4 h-4" />
          Novo Colaborador
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : colaboradores.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>Nenhum colaborador cadastrado</p>
          <p className="text-sm mt-1">Clique em "Novo Colaborador" para adicionar</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Permissões</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {colaboradores.map(col => (
                <tr key={col.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">{col.nome}</td>
                  <td className="px-4 py-3 text-gray-600">{col.email}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1 flex-wrap">
                      {JSON.parse(col.permissoes || '[]').map(p => (
                        <span key={p} className="px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700 font-medium capitalize">
                          {p}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${col.ativo ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {col.ativo ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openModal(col)} className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100" title="Editar">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleToggleAtivo(col)} className={`p-1.5 rounded-lg transition-colors ${col.ativo ? 'text-orange-500 hover:bg-orange-50' : 'text-green-500 hover:bg-green-50'}`} title={col.ativo ? 'Desativar' : 'Ativar'}>
                        {col.ativo ? <Ban className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                      </button>
                      <button onClick={() => handleDelete(col)} className="p-1.5 rounded-lg text-red-500 hover:bg-red-50" title="Remover">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal criar/editar */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              {editing ? 'Editar Colaborador' : 'Novo Colaborador'}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                <input
                  type="text"
                  value={form.nome}
                  onChange={e => setForm(f => ({ ...f, nome: e.target.value }))}
                  className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-primary"
                  placeholder="Nome do colaborador"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  disabled={!!editing}
                  className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-primary disabled:bg-gray-50 disabled:text-gray-400"
                  placeholder="email@exemplo.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Senha {editing && <span className="text-gray-400 font-normal">(deixe em branco para não alterar)</span>}
                </label>
                <input
                  type="password"
                  value={form.senha}
                  onChange={e => setForm(f => ({ ...f, senha: e.target.value }))}
                  className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-primary"
                  placeholder="Mínimo 6 caracteres"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Permissões</label>
                <div className="flex gap-3">
                  {ABAS_DISPONIVEIS.map(aba => (
                    <label key={aba.id} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.permissoes.includes(aba.id)}
                        onChange={() => togglePermissao(aba.id)}
                        className="rounded"
                      />
                      <span className="text-sm text-gray-700">{aba.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 px-4 py-2.5 rounded-xl bg-primary text-white text-sm font-medium hover:bg-primary-hover disabled:opacity-50"
              >
                {saving ? 'Salvando...' : editing ? 'Salvar' : 'Criar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AdminBackoffice = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('users');
  const [adminInfo, setAdminInfo] = useState({ role: 'admin', permissoes: null }); // role: 'admin' | 'colaborador'
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Lembretes admin state
  const [adminLembretes, setAdminLembretes] = useState([]);
  const [adminLembretesLoading, setAdminLembretesLoading] = useState(false);
  const [adminLembretesFilter, setAdminLembretesFilter] = useState('todos');
  const [adminLembretesOrigemFilter, setAdminLembretesOrigemFilter] = useState('todos_origem');
  const [adminLembretesSearch, setAdminLembretesSearch] = useState('');

  // Paginação
  const ITEMS_PER_PAGE = 20;
  const [usersPage, setUsersPage] = useState(1);
  const [lembretesPage, setLembretesPage] = useState(1);

  // Profissões state
  const [profissoes, setProfissoes] = useState([]);
  const [profLoading, setProfLoading] = useState(false);
  const [showProfModal, setShowProfModal] = useState(false);
  const [editingProf, setEditingProf] = useState(null);
  const [profForm, setProfForm] = useState({ nome: '', tempo_retorno_dias: 365 });

  // Generic CRUD state for seguro tables
  const [crudTab, setCrudTab] = useState({ items: [], loading: false, showModal: false, editing: null, form: { nome: '' } });

  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    title: '',
    description: '',
    actionLabel: '',
    actionVariant: 'default', // 'default', 'destructive', 'success'
    onConfirm: null,
    loading: false,
    icon: null,
  });

  // Cupons state
  const [cupons, setCupons] = useState([]);
  const [cuponsLoading, setCuponsLoading] = useState(false);
  const [showCupomModal, setShowCupomModal] = useState(false);
  const [editingCupom, setEditingCupom] = useState(null);
  const [cupomForm, setCupomForm] = useState({
    codigo: '',
    desconto_percent: 100,
    duracao_meses: '',  // NOVO: duração em meses
    validade_inicio: '',
    validade_fim: '',
    limite_usos: '',
    descricao: ''
  });
  const [showUsosModal, setShowUsosModal] = useState(false);
  const [usosData, setUsosData] = useState({ cupom: null, usos: [] });
  const [usosLoading, setUsosLoading] = useState(false);

  // CNAE-Cupons state
  const [cnaeCupons, setCnaeCupons] = useState([]);
  const [cnaeCuponsLoading, setCnaeCuponsLoading] = useState(false);
  const [showCnaeModal, setShowCnaeModal] = useState(false);
  const [cnaeForm, setCnaeForm] = useState({ cnae: '', descricao_cnae: '', cupom_id: '' });

  // Serviços state
  const [servicos, setServicos] = useState([]);
  const [servicosLoading, setServicosLoading] = useState(false);
  const [showServicoModal, setShowServicoModal] = useState(false);
  const [editingServico, setEditingServico] = useState(null);
  const [servicoForm, setServicoForm] = useState({ nome: '', profissao_id: '' });

  // Monitoramento state
  const [monitoramento, setMonitoramento] = useState(null);
  const [monitoramentoLoading, setMonitoramentoLoading] = useState(false);
  const [historicoNotificacoes, setHistoricoNotificacoes] = useState([]);
  const [historicoLoading, setHistoricoLoading] = useState(false);
  const [showTesteModal, setShowTesteModal] = useState(false);
  const [testeForm, setTesteForm] = useState({ email: '', telefone: '', cliente_nome: 'Teste Admin', tipo_servico: 'Serviço Teste', dias_restantes: 7 });
  const [testeEnviando, setTesteEnviando] = useState(false);
  const [cronDisparando, setCronDisparando] = useState(false);
  const [historicoFiltro, setHistoricoFiltro] = useState({ canal: '', status: '' });

  // Templates state
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesConfig, setTemplatesConfig] = useState({ profissoes: [], tipos_mensagem: [], variaveis: [] });
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({ tipo_profissao: '', tipo_mensagem: '', assunto: '', conteudo: '', ativo: true });
  const [templatePreview, setTemplatePreview] = useState('');
  const [templateFiltro, setTemplateFiltro] = useState({ profissao: '', tipo: '' });

  useEffect(() => {
    const adminToken = localStorage.getItem('admin_token');
    if (!adminToken) {
      navigate('/admin-lembrou');
      return;
    }
    // Carregar info do admin/colaborador
    const adminUserData = localStorage.getItem('admin_user');
    if (adminUserData) {
      try {
        const info = JSON.parse(adminUserData);
        setAdminInfo({ role: info.role || 'admin', permissoes: info.permissoes || null, nome: info.nome || 'Admin' });
      } catch (e) {
        setAdminInfo({ role: 'admin', permissoes: null });
      }
    } else {
      // Fallback: decodificar token para saber o role
      try {
        const payload = JSON.parse(atob(adminToken.split('.')[1]));
        const isColaborador = payload.user_id?.startsWith('colaborador:');
        setAdminInfo({ role: isColaborador ? 'colaborador' : 'admin', permissoes: null });
      } catch (e) {
        setAdminInfo({ role: 'admin', permissoes: null });
      }
    }
    fetchData();
  }, []);

  // Carregar dados quando mudar de tab
  useEffect(() => {
    if (activeTab === 'profissoes') {
      fetchProfissoes();
    } else if (activeTab === 'servicos') {
      fetchServicos();
      if (profissoes.length === 0) fetchProfissoes();
    } else if (activeTab === 'cupons') {
      fetchCupons();
    } else if (activeTab === 'monitoramento') {
      fetchMonitoramento();
      fetchHistoricoNotificacoes();
    } else if (activeTab === 'templates') {
      fetchTemplates();
      fetchTemplatesConfig();
    } else if (['tipos-seguro', 'seguradoras', 'corretoras'].includes(activeTab)) {
      fetchCrudItems(activeTab);
    } else if (activeTab === 'lembretes') {
      fetchAdminLembretes();
    }
  }, [activeTab]);

  const fetchData = async () => {
    try {
      const adminToken = localStorage.getItem('admin_token');
      const config = { headers: { Authorization: `Bearer ${adminToken}` } };

      const [statsRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, config),
        axios.get(`${API}/admin/users`, config)
      ]);

      setStats(statsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
      if (error.response?.status === 403) {
        navigate('/admin-lembrou');
      }
    } finally {
      setLoading(false);
    }
  };

  // ==================== PROFISSÕES CRUD ====================
  const fetchProfissoes = async () => {
    setProfLoading(true);
    try {
      const adminToken = localStorage.getItem('admin_token');
      const res = await axios.get(`${API}/admin/profissoes`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setProfissoes(res.data);
    } catch (error) {
      toast.error('Erro ao carregar profissoes');
    } finally {
      setProfLoading(false);
    }
  };

  const openProfForm = (prof = null) => {
    if (prof) {
      setEditingProf(prof);
      setProfForm({ nome: prof.nome, tempo_retorno_dias: prof.tempo_retorno_dias || 365 });
    } else {
      setEditingProf(null);
      setProfForm({ nome: '', tempo_retorno_dias: 365 });
    }
    setShowProfModal(true);
  };

  const saveProfissao = async () => {
    if (!profForm.nome.trim()) {
      toast.error('Nome da profissao e obrigatorio');
      return;
    }
    const adminToken = localStorage.getItem('admin_token');
    const config = { headers: { Authorization: `Bearer ${adminToken}` } };
    try {
      if (editingProf) {
        await axios.put(`${API}/admin/profissoes/${editingProf.id}`, profForm, config);
        toast.success('Profissao atualizada!');
      } else {
        await axios.post(`${API}/admin/profissoes`, profForm, config);
        toast.success('Profissao criada!');
      }
      setShowProfModal(false);
      fetchProfissoes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar profissao');
    }
  };

  const deleteProfissao = (prof) => {
    openConfirm({
      title: 'Excluir Profissao',
      description: `Deseja remover a profissao "${prof.nome}"? Ela nao aparecera mais para novos cadastros.`,
      actionLabel: 'Excluir',
      actionVariant: 'destructive',
      icon: 'delete',
      onConfirm: async () => {
        const adminToken = localStorage.getItem('admin_token');
        await axios.delete(`${API}/admin/profissoes/${prof.id}`, {
          headers: { Authorization: `Bearer ${adminToken}` }
        });
        toast.success('Profissao removida!');
        fetchProfissoes();
      },
    });
  };

  useEffect(() => {
    if (activeTab === 'profissoes') fetchProfissoes();
    if (['tipos-seguro', 'seguradoras', 'corretoras'].includes(activeTab)) fetchCrudItems(activeTab);
    if (activeTab === 'cupons') fetchCupons();
    if (activeTab === 'cnae-cupons') {
      fetchCnaeCupons();
      fetchCupons(); // Precisa dos cupons para o select
    }
  }, [activeTab]);

  // ==================== CNAE-CUPONS CRUD ====================
  const fetchCnaeCupons = async () => {
    setCnaeCuponsLoading(true);
    try {
      const adminToken = localStorage.getItem('admin_token');
      const res = await axios.get(`${API}/admin/cnae-cupons`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setCnaeCupons(res.data);
    } catch (error) {
      toast.error('Erro ao carregar CNAEs');
    } finally {
      setCnaeCuponsLoading(false);
    }
  };

  const saveCnaeCupom = async () => {
    if (!cnaeForm.cnae.trim()) {
      toast.error('CNAE é obrigatório');
      return;
    }
    if (!cnaeForm.cupom_id) {
      toast.error('Selecione um cupom');
      return;
    }

    try {
      const adminToken = localStorage.getItem('admin_token');
      await axios.post(`${API}/admin/cnae-cupons`, cnaeForm, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success('CNAE associado com sucesso!');
      setShowCnaeModal(false);
      setCnaeForm({ cnae: '', descricao_cnae: '', cupom_id: '' });
      fetchCnaeCupons();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar');
    }
  };

  const deleteCnaeCupom = (id, cnae) => {
    openConfirm({
      title: 'Remover Associação',
      description: `Tem certeza que deseja remover a associação do CNAE ${cnae} com o cupom?`,
      actionLabel: 'Remover',
      actionVariant: 'destructive',
      icon: 'trash',
      onConfirm: async () => {
        const adminToken = localStorage.getItem('admin_token');
        await axios.delete(`${API}/admin/cnae-cupons/${id}`, {
          headers: { Authorization: `Bearer ${adminToken}` }
        });
        toast.success('Associação removida!');
        fetchCnaeCupons();
      }
    });
  };

  // ==================== CUPONS CRUD ====================
  const fetchCupons = async () => {
    setCuponsLoading(true);
    try {
      const adminToken = localStorage.getItem('admin_token');
      const res = await axios.get(`${API}/admin/cupons`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setCupons(res.data);
    } catch (error) {
      toast.error('Erro ao carregar cupons');
    } finally {
      setCuponsLoading(false);
    }
  };

  const openCupomForm = (cupom = null) => {
    if (cupom) {
      setEditingCupom(cupom);
      setCupomForm({
        codigo: cupom.codigo,
        desconto_percent: cupom.desconto_percent,
        duracao_meses: cupom.duracao_meses || '',  // NOVO
        validade_inicio: cupom.validade_inicio || '',
        validade_fim: cupom.validade_fim || '',
        limite_usos: cupom.limite_usos || '',
        descricao: cupom.descricao || ''
      });
    } else {
      setEditingCupom(null);
      setCupomForm({
        codigo: '',
        desconto_percent: 100,
        duracao_meses: '',  // NOVO
        validade_inicio: '',
        validade_fim: '',
        limite_usos: '',
        descricao: ''
      });
    }
    setShowCupomModal(true);
  };

  const saveCupom = async () => {
    if (!cupomForm.codigo.trim()) {
      toast.error('Código do cupom é obrigatório');
      return;
    }
    if (cupomForm.desconto_percent < 0 || cupomForm.desconto_percent > 100) {
      toast.error('Desconto deve ser entre 0 e 100%');
      return;
    }

    const adminToken = localStorage.getItem('admin_token');
    const config = { headers: { Authorization: `Bearer ${adminToken}` } };
    
    // Validação: cupons 100% devem ser permanentes
    if (cupomForm.desconto_percent === 100 && cupomForm.duracao_meses !== '' && cupomForm.duracao_meses !== null) {
      toast.error('Cupons de 100% devem ser permanentes (deixe duração vazia)');
      return;
    }
    
    // Validação: duração mínima de 1 mês
    if (cupomForm.duracao_meses !== '' && parseInt(cupomForm.duracao_meses) < 1) {
      toast.error('Duração mínima é 1 mês');
      return;
    }

    const payload = {
      ...cupomForm,
      duracao_meses: cupomForm.duracao_meses ? parseInt(cupomForm.duracao_meses) : null,  // NOVO
      limite_usos: cupomForm.limite_usos ? parseInt(cupomForm.limite_usos) : null,
      validade_inicio: cupomForm.validade_inicio || null,
      validade_fim: cupomForm.validade_fim || null
    };

    try {
      if (editingCupom) {
        await axios.put(`${API}/admin/cupons/${editingCupom.id}`, payload, config);
        toast.success('Cupom atualizado!');
      } else {
        await axios.post(`${API}/admin/cupons`, payload, config);
        toast.success('Cupom criado!');
      }
      setShowCupomModal(false);
      fetchCupons();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar cupom');
    }
  };

  const toggleCupomStatus = async (cupom) => {
    const adminToken = localStorage.getItem('admin_token');
    try {
      await axios.put(
        `${API}/admin/cupons/${cupom.id}`,
        { ativo: !cupom.ativo },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success(cupom.ativo ? 'Cupom desativado!' : 'Cupom ativado!');
      fetchCupons();
    } catch (error) {
      toast.error('Erro ao alterar status do cupom');
    }
  };

  const deleteCupom = (cupom) => {
    openConfirm({
      title: 'Excluir Cupom',
      description: cupom.usos_realizados > 0 
        ? `Este cupom já foi usado ${cupom.usos_realizados} vez(es). Ele será desativado em vez de excluído.`
        : `Deseja excluir o cupom "${cupom.codigo}"?`,
      actionLabel: cupom.usos_realizados > 0 ? 'Desativar' : 'Excluir',
      actionVariant: 'destructive',
      icon: 'delete',
      onConfirm: async () => {
        const adminToken = localStorage.getItem('admin_token');
        await axios.delete(`${API}/admin/cupons/${cupom.id}`, {
          headers: { Authorization: `Bearer ${adminToken}` }
        });
        toast.success(cupom.usos_realizados > 0 ? 'Cupom desativado!' : 'Cupom excluído!');
        fetchCupons();
      },
    });
  };

  const viewCupomUsos = async (cupom) => {
    setUsosLoading(true);
    setUsosData({ cupom, usos: [] });
    setShowUsosModal(true);
    
    try {
      const adminToken = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/cupons/${cupom.id}/usos`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setUsosData({ cupom, usos: response.data });
    } catch (error) {
      toast.error('Erro ao carregar usos do cupom');
    } finally {
      setUsosLoading(false);
    }
  };

  // ==================== SERVIÇOS ====================
  const fetchServicos = async () => {
    setServicosLoading(true);
    try {
      const adminToken = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/servicos`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setServicos(response.data);
    } catch (error) {
      toast.error('Erro ao carregar serviços');
    } finally {
      setServicosLoading(false);
    }
  };

  const openServicoModal = (servico = null) => {
    if (servico) {
      setEditingServico(servico);
      setServicoForm({ nome: servico.nome, profissao_id: servico.profissao_id });
    } else {
      setEditingServico(null);
      setServicoForm({ nome: '', profissao_id: '' });
    }
    setShowServicoModal(true);
  };

  const saveServico = async () => {
    if (!servicoForm.nome.trim()) {
      toast.error('Nome do serviço é obrigatório');
      return;
    }
    if (!servicoForm.profissao_id) {
      toast.error('Selecione uma profissão');
      return;
    }

    const adminToken = localStorage.getItem('admin_token');
    const config = { headers: { Authorization: `Bearer ${adminToken}` } };

    try {
      if (editingServico) {
        await axios.put(`${API}/admin/servicos/${editingServico.id}`, servicoForm, config);
        toast.success('Serviço atualizado!');
      } else {
        await axios.post(`${API}/admin/servicos`, servicoForm, config);
        toast.success('Serviço criado!');
      }
      setShowServicoModal(false);
      fetchServicos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar serviço');
    }
  };

  const deleteServico = (servico) => {
    openConfirm({
      title: 'Excluir Serviço',
      description: `Deseja excluir o serviço "${servico.nome}"?`,
      actionLabel: 'Excluir',
      actionVariant: 'destructive',
      icon: 'delete',
      onConfirm: async () => {
        const adminToken = localStorage.getItem('admin_token');
        await axios.delete(`${API}/admin/servicos/${servico.id}`, {
          headers: { Authorization: `Bearer ${adminToken}` }
        });
        toast.success('Serviço excluído!');
        fetchServicos();
      },
    });
  };

  // ==================== LEMBRETES ADMIN ====================
  const fetchAdminLembretes = async () => {
    setAdminLembretesLoading(true);
    try {
      const adminToken = localStorage.getItem('admin_token');
      const res = await axios.get(`${API}/admin/lembretes-todos`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setAdminLembretes(res.data || []);
    } catch (error) {
      toast.error('Erro ao carregar lembretes');
    } finally {
      setAdminLembretesLoading(false);
    }
  };

  // ==================== MONITORAMENTO NOTIFICAÇÕES ====================
  const fetchMonitoramento = async () => {
    setMonitoramentoLoading(true);
    try {
      const adminToken = localStorage.getItem('admin_token');
      const res = await axios.get(`${API}/admin/monitoramento/status`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setMonitoramento(res.data);
    } catch (error) {
      toast.error('Erro ao carregar status do monitoramento');
    } finally {
      setMonitoramentoLoading(false);
    }
  };

  const fetchHistoricoNotificacoes = async () => {
    setHistoricoLoading(true);
    try {
      const adminToken = localStorage.getItem('admin_token');
      const params = new URLSearchParams();
      params.append('limite', '50');
      if (historicoFiltro.canal) params.append('canal', historicoFiltro.canal);
      if (historicoFiltro.status) params.append('status', historicoFiltro.status);
      
      const res = await axios.get(`${API}/admin/monitoramento/historico?${params}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setHistoricoNotificacoes(res.data);
    } catch (error) {
      toast.error('Erro ao carregar histórico');
    } finally {
      setHistoricoLoading(false);
    }
  };

  const enviarTeste = async () => {
    if (!testeForm.email && !testeForm.telefone) {
      toast.error('Informe email ou telefone para teste');
      return;
    }
    setTesteEnviando(true);
    try {
      const adminToken = localStorage.getItem('admin_token');
      const res = await axios.post(`${API}/admin/monitoramento/testar-envio`, testeForm, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      
      if (res.data.success) {
        toast.success('Teste enviado com sucesso!');
        if (res.data.email_enviado) toast.success(`Email enviado para ${testeForm.email}`);
        if (res.data.whatsapp_enviado) toast.success(`WhatsApp enviado para ${testeForm.telefone}`);
      } else {
        toast.error('Falha no envio do teste');
      }
      
      if (res.data.erros?.length > 0) {
        res.data.erros.forEach(erro => toast.error(erro));
      }
      
      setShowTesteModal(false);
      fetchHistoricoNotificacoes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar teste');
    } finally {
      setTesteEnviando(false);
    }
  };

  const dispararCron = async (gatilho = null) => {
    const gatilhoLabel = gatilho === null ? 'todos os gatilhos (D-15, D-7, D-0)' : `D-${gatilho}`;
    
    openConfirm({
      title: 'Disparar Lembretes',
      description: `Deseja disparar os lembretes para ${gatilhoLabel}? Isso enviará emails e WhatsApps para todos os lembretes pendentes.`,
      actionLabel: 'Disparar Agora',
      actionVariant: 'default',
      icon: 'send',
      onConfirm: async () => {
        setCronDisparando(true);
        try {
          const adminToken = localStorage.getItem('admin_token');
          const res = await axios.post(`${API}/admin/monitoramento/disparar-cron`, 
            { gatilho },
            { headers: { Authorization: `Bearer ${adminToken}` } }
          );
          
          toast.success(`Cron executado! Emails: ${res.data.enviados_email}, WhatsApps: ${res.data.enviados_whatsapp}`);
          
          if (res.data.erros > 0) {
            toast.warning(`${res.data.erros} erro(s) durante o envio`);
          }
          
          fetchMonitoramento();
          fetchHistoricoNotificacoes();
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Erro ao disparar cron');
        } finally {
          setCronDisparando(false);
        }
      },
    });
  };

  const formatarDataHora = (dataISO) => {
    if (!dataISO) return '-';
    try {
      return new Date(dataISO).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dataISO;
    }
  };

  const formatarDataBR = (dataISO) => {
    if (!dataISO) return '-';
    try {
      const [ano, mes, dia] = dataISO.split('-');
      return `${dia}/${mes}/${ano}`;
    } catch (e) {
      return dataISO;
    }
  };

  // ==================== TEMPLATES CRUD ====================
  const fetchTemplatesConfig = async () => {
    try {
      const adminToken = localStorage.getItem('admin_token');
      const res = await axios.get(`${API}/admin/templates/config`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setTemplatesConfig(res.data);
    } catch (error) {
      toast.error('Erro ao carregar configurações');
    }
  };

  const fetchTemplates = async () => {
    setTemplatesLoading(true);
    try {
      const adminToken = localStorage.getItem('admin_token');
      let url = `${API}/admin/templates`;
      const params = new URLSearchParams();
      if (templateFiltro.profissao) params.append('tipo_profissao', templateFiltro.profissao);
      if (templateFiltro.tipo) params.append('tipo_mensagem', templateFiltro.tipo);
      if (params.toString()) url += `?${params.toString()}`;
      
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setTemplates(res.data);
    } catch (error) {
      toast.error('Erro ao carregar templates');
    } finally {
      setTemplatesLoading(false);
    }
  };

  const openTemplateForm = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setTemplateForm({
        tipo_profissao: template.tipo_profissao,
        tipo_mensagem: template.tipo_mensagem,
        assunto: template.assunto || '',
        conteudo: template.conteudo,
        ativo: template.ativo === 1
      });
      setTemplatePreview(template.conteudo);
    } else {
      setEditingTemplate(null);
      setTemplateForm({ tipo_profissao: '', tipo_mensagem: '', assunto: '', conteudo: '', ativo: true });
      setTemplatePreview('');
    }
    setShowTemplateModal(true);
  };

  const previewTemplate = async (conteudo) => {
    if (!conteudo) {
      setTemplatePreview('');
      return;
    }
    try {
      const adminToken = localStorage.getItem('admin_token');
      const res = await axios.post(
        `${API}/admin/templates/preview`,
        { conteudo },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      setTemplatePreview(res.data.preview);
    } catch (error) {
      setTemplatePreview(conteudo);
    }
  };

  const saveTemplate = async () => {
    if (!templateForm.tipo_profissao || !templateForm.tipo_mensagem || !templateForm.conteudo.trim()) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    if (templateForm.tipo_mensagem.startsWith('email_') && !templateForm.assunto.trim()) {
      toast.error('Templates de email precisam de assunto');
      return;
    }

    const adminToken = localStorage.getItem('admin_token');
    const config = { headers: { Authorization: `Bearer ${adminToken}` } };

    try {
      if (editingTemplate) {
        await axios.put(`${API}/admin/templates/${editingTemplate.id}`, templateForm, config);
        toast.success('Template atualizado!');
      } else {
        await axios.post(`${API}/admin/templates`, templateForm, config);
        toast.success('Template criado!');
      }
      setShowTemplateModal(false);
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar');
    }
  };

  const toggleTemplate = async (template) => {
    const adminToken = localStorage.getItem('admin_token');
    try {
      await axios.post(
        `${API}/admin/templates/${template.id}/toggle`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success(template.ativo === 1 ? 'Template desativado' : 'Template ativado');
      fetchTemplates();
    } catch (error) {
      toast.error('Erro ao alternar status');
    }
  };

  const deleteTemplate = (template) => {
    if (template.tipo_profissao === 'padrao') {
      toast.error('Não é possível excluir templates padrão');
      return;
    }
    openConfirm({
      title: 'Excluir Template',
      description: `Tem certeza que deseja excluir o template "${template.tipo_profissao} - ${template.tipo_mensagem}"?`,
      actionLabel: 'Excluir',
      actionVariant: 'destructive',
      icon: <Trash2 className="w-6 h-6 text-red-500" />,
      onConfirm: async () => {
        const adminToken = localStorage.getItem('admin_token');
        try {
          await axios.delete(`${API}/admin/templates/${template.id}`, {
            headers: { Authorization: `Bearer ${adminToken}` }
          });
          toast.success('Template excluído');
          fetchTemplates();
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Erro ao excluir');
        }
      },
    });
  };

  const getProfissaoNome = (id) => {
    const p = templatesConfig.profissoes.find(p => p.id === id);
    return p ? p.nome : id;
  };

  const getTipoMensagemNome = (id) => {
    const t = templatesConfig.tipos_mensagem.find(t => t.id === id);
    return t ? t.nome : id;
  };

  const getTipoMensagemCanal = (id) => {
    const t = templatesConfig.tipos_mensagem.find(t => t.id === id);
    return t ? t.canal : 'whatsapp';
  };

  // ==================== CRUD GENÉRICO SEGUROS ====================
  const crudConfig = {
    'tipos-seguro': { label: 'Tipo de Seguro', icon: FileText },
    'seguradoras': { label: 'Seguradora', icon: Shield },
    'corretoras': { label: 'Corretora', icon: Building2 },
  };

  const fetchCrudItems = async (tab) => {
    setCrudTab(prev => ({ ...prev, loading: true }));
    try {
      const adminToken = localStorage.getItem('admin_token');
      const res = await axios.get(`${API}/admin/${tab}`, { headers: { Authorization: `Bearer ${adminToken}` } });
      setCrudTab(prev => ({ ...prev, items: res.data, loading: false }));
    } catch (e) {
      toast.error('Erro ao carregar dados');
      setCrudTab(prev => ({ ...prev, loading: false }));
    }
  };

  const openCrudForm = (item = null) => {
    setCrudTab(prev => ({
      ...prev,
      showModal: true,
      editing: item,
      form: { nome: item ? item.nome : '' }
    }));
  };

  const saveCrudItem = async () => {
    if (!crudTab.form.nome.trim()) { toast.error('Nome e obrigatorio'); return; }
    const adminToken = localStorage.getItem('admin_token');
    const config = { headers: { Authorization: `Bearer ${adminToken}` } };
    try {
      if (crudTab.editing) {
        await axios.put(`${API}/admin/${activeTab}/${crudTab.editing.id}`, crudTab.form, config);
        toast.success('Atualizado!');
      } else {
        await axios.post(`${API}/admin/${activeTab}`, crudTab.form, config);
        toast.success('Criado!');
      }
      setCrudTab(prev => ({ ...prev, showModal: false }));
      fetchCrudItems(activeTab);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar');
    }
  };

  const deleteCrudItem = (item) => {
    const cfg = crudConfig[activeTab];
    openConfirm({
      title: `Excluir ${cfg.label}`,
      description: `Deseja remover "${item.nome}"?`,
      actionLabel: 'Excluir',
      actionVariant: 'destructive',
      icon: 'delete',
      onConfirm: async () => {
        const adminToken = localStorage.getItem('admin_token');
        await axios.delete(`${API}/admin/${activeTab}/${item.id}`, { headers: { Authorization: `Bearer ${adminToken}` } });
        toast.success('Removido!');
        fetchCrudItems(activeTab);
      },
    });
  };

  const openConfirm = ({ title, description, actionLabel, actionVariant, onConfirm, icon }) => {
    setConfirmModal({
      open: true,
      title,
      description,
      actionLabel,
      actionVariant: actionVariant || 'default',
      onConfirm,
      loading: false,
      icon: icon || null,
    });
  };

  const executeConfirm = async () => {
    if (!confirmModal.onConfirm) return;
    setConfirmModal(prev => ({ ...prev, loading: true }));
    try {
      await confirmModal.onConfirm();
    } finally {
      setConfirmModal(prev => ({ ...prev, open: false, loading: false }));
    }
  };

  const closeConfirm = () => {
    if (!confirmModal.loading) {
      setConfirmModal(prev => ({ ...prev, open: false }));
    }
  };

  const handleViewDetails = (user) => {
    setSelectedUser(user);
    setShowDetailsModal(true);
  };

  const handleUpdateStatus = (userId, action) => {
    const userName = users.find(u => u.id === userId)?.nome_empresa || 'Usuário';

    if (action === 'freeze') {
      openConfirm({
        title: 'Congelar Conta',
        description: `Tem certeza que deseja CONGELAR a conta de "${userName}"? O usuário não poderá acessar o sistema enquanto a conta estiver congelada.`,
        actionLabel: 'Congelar Conta',
        actionVariant: 'destructive',
        icon: 'freeze',
        onConfirm: async () => {
          const adminToken = localStorage.getItem('admin_token');
          await axios.put(
            `${API}/admin/users/${userId}/status`,
            { action: 'freeze' },
            { headers: { Authorization: `Bearer ${adminToken}` } }
          );
          toast.success('Conta congelada com sucesso!');
          fetchData();
        },
      });
    } else {
      openConfirm({
        title: 'Descongelar Conta',
        description: `Deseja DESCONGELAR a conta de "${userName}"? O usuário voltará a ter acesso ao sistema.`,
        actionLabel: 'Descongelar Conta',
        actionVariant: 'success',
        icon: 'activate',
        onConfirm: async () => {
          const adminToken = localStorage.getItem('admin_token');
          await axios.put(
            `${API}/admin/users/${userId}/status`,
            { action: 'activate' },
            { headers: { Authorization: `Bearer ${adminToken}` } }
          );
          toast.success('Conta descongelada com sucesso!');
          fetchData();
        },
      });
    }
  };

  const handleUpdatePlan = (userId, plan) => {
    const userName = users.find(u => u.id === userId)?.nome_empresa || 'Usuário';

    if (plan === 'PRO') {
      openConfirm({
        title: 'Upgrade para PRO',
        description: `Deseja fazer o UPGRADE da conta de "${userName}" para o Plano PRO? O usuário terá acesso a lembretes ilimitados e funcionalidades exclusivas.`,
        actionLabel: 'Confirmar Upgrade',
        actionVariant: 'success',
        icon: 'upgrade',
        onConfirm: async () => {
          const adminToken = localStorage.getItem('admin_token');
          await axios.put(
            `${API}/admin/users/${userId}/plan`,
            { plan: 'PRO' },
            { headers: { Authorization: `Bearer ${adminToken}` } }
          );
          toast.success('Plano atualizado para PRO!');
          fetchData();
        },
      });
    } else {
      openConfirm({
        title: 'Downgrade para FREE',
        description: `Deseja fazer o DOWNGRADE da conta de "${userName}" para o Plano FREE? O limite de 20 lembretes será aplicado.`,
        actionLabel: 'Confirmar Downgrade',
        actionVariant: 'destructive',
        icon: 'downgrade',
        onConfirm: async () => {
          const adminToken = localStorage.getItem('admin_token');
          await axios.put(
            `${API}/admin/users/${userId}/plan`,
            { plan: 'FREE' },
            { headers: { Authorization: `Bearer ${adminToken}` } }
          );
          toast.success('Plano atualizado para FREE');
          fetchData();
        },
      });
    }
  };

  const handleDelete = (userId) => {
    const userName = users.find(u => u.id === userId)?.nome_empresa || 'Usuário';

    openConfirm({
      title: 'Excluir Usuário',
      description: `ATENÇÃO: Esta acao e irreversivel!\n\nIsso deletara o usuario "${userName}" e TODOS os seus lembretes permanentemente.\n\nDeseja continuar?`,
      actionLabel: 'Excluir Permanentemente',
      actionVariant: 'destructive',
      icon: 'delete',
      onConfirm: async () => {
        const adminToken = localStorage.getItem('admin_token');
        await axios.delete(`${API}/admin/users/${userId}`, {
          headers: { Authorization: `Bearer ${adminToken}` }
        });
        toast.success('Usuario deletado com sucesso');
        fetchData();
      },
    });
  };

  const handleExportCSV = () => {
    if (filteredUsers.length === 0) {
      toast.error('Nenhum usuario para exportar');
      return;
    }

    try {
      const escapeCSV = (val) => {
        const str = String(val ?? '');
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      const headers = ['Nome/Empresa', 'Email', 'Tipo', 'Profissao', 'Plano', 'Lembretes', 'Telefone', 'Status'];
      const rows = filteredUsers.map(user => [
        escapeCSV(user.nome_empresa),
        escapeCSV(user.email),
        escapeCSV(user.tipo_usuario || 'PJ'),
        escapeCSV(user.tipo_profissao),
        escapeCSV(user.plano),
        user.contador_lembretes || 0,
        escapeCSV(user.telefone || ''),
        escapeCSV(user.status || 'active')
      ].join(','));

      const csv = [headers.join(','), ...rows].join('\n');
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.download = `usuarios_lembrou_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('CSV exportado com sucesso!');
    } catch (error) {
      console.error('CSV export error:', error);
      toast.error('Erro ao exportar CSV. Tente novamente.');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/');
  };

  const filteredUsers = users.filter(user => {
    let matchesFilter = true;
    if (filter === 'free') matchesFilter = user.plano === 'FREE';
    else if (filter === 'pro') matchesFilter = user.plano === 'PRO';
    else if (filter === 'pj') matchesFilter = user.tipo_usuario === 'PJ' || !user.tipo_usuario;
    else if (filter === 'pf') matchesFilter = user.tipo_usuario === 'PF';

    const matchesSearch = searchQuery === '' ||
      (user.nome_empresa || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (user.email || '').toLowerCase().includes(searchQuery.toLowerCase());

    return matchesFilter && matchesSearch;
  });

  const usersTotalPages = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);
  const paginatedUsers = filteredUsers.slice((usersPage - 1) * ITEMS_PER_PAGE, usersPage * ITEMS_PER_PAGE);

  const getConfirmIcon = () => {
    switch (confirmModal.icon) {
      case 'upgrade': return <Crown className="w-6 h-6 text-green-600" />;
      case 'downgrade': return <TrendingUp className="w-6 h-6 text-red-500 rotate-180" />;
      case 'freeze': return <Snowflake className="w-6 h-6 text-blue-600" />;
      case 'activate': return <CheckCircle className="w-6 h-6 text-green-600" />;
      case 'delete': return <ShieldAlert className="w-6 h-6 text-red-600" />;
      case 'send': return <Send className="w-6 h-6 text-primary" />;
      default: return null;
    }
  };

  const getActionButtonClass = () => {
    switch (confirmModal.actionVariant) {
      case 'destructive': return 'bg-red-600 hover:bg-red-700 text-white';
      case 'success': return 'bg-green-600 hover:bg-green-700 text-white';
      default: return '';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-lg text-gray-600">Carregando...</div>
      </div>
    );
  }

  // Menu items para sidebar
  const isAdmin = adminInfo.role === 'admin';
  const temPermissao = (aba) => isAdmin || (adminInfo.permissoes || []).includes(aba);

  const menuItems = [
    { id: 'users', label: 'Usuários', icon: Users },
    { id: 'lembretes', label: 'Lembretes', icon: Bell },
    ...(isAdmin ? [
      { id: 'profissoes', label: 'Profissões', icon: Briefcase },
      { id: 'tipos-seguro', label: 'Tipos Seguro', icon: FileText },
      { id: 'seguradoras', label: 'Seguradoras', icon: Shield },
      { id: 'corretoras', label: 'Corretoras', icon: Building2 },
      { id: 'servicos', label: 'Serviços', icon: Bell },
      { id: 'cupons', label: 'Cupons', icon: Ticket },
      { id: 'cnae-cupons', label: 'CNAE→Cupom', icon: Gift },
      { id: 'templates', label: 'Templates', icon: MessageCircle },
      { id: 'monitoramento', label: 'Monitoramento', icon: Activity },
      { id: 'colaboradores', label: 'Colaboradores', icon: Users },
    ] : [])
  ].filter(item => temPermissao(item.id));

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside className="w-64 bg-gray-900 text-white flex flex-col min-h-screen fixed left-0 top-0 z-40">
        {/* Logo */}
        <div className="p-4 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <img src="/icon-lembrou.jpg" alt="Lembrou?" className="w-10 h-10 rounded-xl" />
            <div>
              <h1 className="font-bold text-lg">Backoffice</h1>
              <p className="text-xs text-gray-400">Lembrou Admin</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {menuItems.map(item => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                data-testid={`tab-${item.id}`}
                onClick={() => setActiveTab(item.id)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  activeTab === item.id
                    ? 'bg-primary text-white'
                    : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                }`}
              >
                <Icon className="w-5 h-5" />
                {item.label}
              </button>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-gray-700">
          <Button
            data-testid="admin-logout-button"
            onClick={handleLogout}
            variant="ghost"
            className="w-full justify-start gap-3 rounded-xl text-gray-300 hover:bg-gray-800 hover:text-white"
          >
            <LogOut className="w-5 h-5" />
            Sair
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-64 p-8">
        {/* Page Header */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900">
            {menuItems.find(m => m.id === activeTab)?.label || 'Dashboard'}
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Gerenciamento do sistema Lembrou
          </p>
        </div>

        {activeTab === 'users' && (
        <>
        {/* Stats Cards - Primeira linha */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <Card className="p-4 md:p-6 rounded-2xl border shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-gray-500 mb-1">Total Usuários</p>
                <p className="text-2xl md:text-3xl font-bold text-gray-900">{stats?.total_users || 0}</p>
              </div>
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <Users className="w-5 h-5 md:w-6 md:h-6 text-blue-600" />
              </div>
            </div>
          </Card>

          <Card className="p-4 md:p-6 rounded-2xl border shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-gray-500 mb-1">Plano FREE</p>
                <p className="text-2xl md:text-3xl font-bold text-gray-900">{stats?.total_free || 0}</p>
              </div>
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-gray-100 flex items-center justify-center">
                <Users className="w-5 h-5 md:w-6 md:h-6 text-gray-600" />
              </div>
            </div>
          </Card>

          <Card className="p-4 md:p-6 rounded-2xl border shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-gray-500 mb-1">PRO Pagantes</p>
                <p className="text-2xl md:text-3xl font-bold text-green-600">{stats?.pro_pagantes || 0}</p>
                <p className="text-xs text-gray-400">de {stats?.total_pro || 0} PRO</p>
              </div>
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-green-100 flex items-center justify-center">
                <Crown className="w-5 h-5 md:w-6 md:h-6 text-green-600" />
              </div>
            </div>
          </Card>

          <Card className="p-4 md:p-6 rounded-2xl border shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-gray-500 mb-1">PRO Cupom</p>
                <p className="text-2xl md:text-3xl font-bold text-purple-600">{stats?.pro_cupom || 0}</p>
                <p className="text-xs text-gray-400">cortesia/trial</p>
              </div>
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                <Ticket className="w-5 h-5 md:w-6 md:h-6 text-purple-600" />
              </div>
            </div>
          </Card>
        </div>

        {/* Stats Cards - Segunda linha: Receitas */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="p-4 md:p-6 rounded-2xl border shadow-sm border-l-4 border-l-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-gray-500 mb-1">MRR</p>
                <p className="text-xl md:text-2xl font-bold text-green-600">
                  R$ {stats?.mrr?.toFixed(2) || '0.00'}
                </p>
                <p className="text-xs text-gray-400">receita recorrente</p>
              </div>
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-green-100 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 md:w-6 md:h-6 text-green-600" />
              </div>
            </div>
          </Card>

          <Card className="p-4 md:p-6 rounded-2xl border shadow-sm border-l-4 border-l-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-gray-500 mb-1">Receita Mês</p>
                <p className="text-xl md:text-2xl font-bold text-blue-600">
                  R$ {stats?.receita_mes?.toFixed(2) || '0.00'}
                </p>
                <p className="text-xs text-gray-400">pago este mês</p>
              </div>
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <Calendar className="w-5 h-5 md:w-6 md:h-6 text-blue-600" />
              </div>
            </div>
          </Card>

          <Card className="p-4 md:p-6 rounded-2xl border shadow-sm border-l-4 border-l-emerald-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-gray-500 mb-1">Receita Total</p>
                <p className="text-xl md:text-2xl font-bold text-emerald-600">
                  R$ {stats?.receita_real?.toFixed(2) || '0.00'}
                </p>
                <p className="text-xs text-gray-400">confirmado Stripe</p>
              </div>
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                <DollarSign className="w-5 h-5 md:w-6 md:h-6 text-emerald-600" />
              </div>
            </div>
          </Card>

          <Card className="p-4 md:p-6 rounded-2xl border shadow-sm border-l-4 border-l-orange-400">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-gray-500 mb-1">Projeção</p>
                <p className="text-xl md:text-2xl font-bold text-orange-500">
                  R$ {stats?.revenue_estimate?.toFixed(2) || '0.00'}
                </p>
                <p className="text-xs text-gray-400">se todos pagassem</p>
              </div>
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-orange-100 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 md:w-6 md:h-6 text-orange-500" />
              </div>
            </div>
          </Card>
        </div>

        {/* Search and Actions */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              data-testid="admin-search-input"
              placeholder="Buscar por nome ou email..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setUsersPage(1); }}
              className="pl-10 rounded-xl h-11"
            />
          </div>
          <Button
            data-testid="export-csv-button"
            onClick={handleExportCSV}
            variant="outline"
            className="rounded-xl"
          >
            <Download className="w-4 h-4 mr-2" />
            Exportar CSV
          </Button>
        </div>

        {/* Filters */}
        <div className="flex gap-3 mb-6 flex-wrap">
          <Button
            data-testid="filter-all"
            onClick={() => setFilter('all')}
            variant={filter === 'all' ? 'default' : 'outline'}
            className="rounded-xl"
          >
            Todos ({users.length})
          </Button>
          <Button
            data-testid="filter-pj"
            onClick={() => setFilter('pj')}
            variant={filter === 'pj' ? 'default' : 'outline'}
            className="rounded-xl"
          >
            PJ ({users.filter(u => u.tipo_usuario === 'PJ' || !u.tipo_usuario).length})
          </Button>
          <Button
            data-testid="filter-pf"
            onClick={() => setFilter('pf')}
            variant={filter === 'pf' ? 'default' : 'outline'}
            className="rounded-xl"
          >
            PF ({users.filter(u => u.tipo_usuario === 'PF').length})
          </Button>
          <div className="border-l border-gray-300 mx-2"></div>
          <Button
            data-testid="filter-free"
            onClick={() => setFilter('free')}
            variant={filter === 'free' ? 'default' : 'outline'}
            className="rounded-xl"
          >
            FREE ({users.filter(u => u.plano === 'FREE').length})
          </Button>
          <Button
            data-testid="filter-pro"
            onClick={() => setFilter('pro')}
            variant={filter === 'pro' ? 'default' : 'outline'}
            className="rounded-xl"
          >
            PRO ({users.filter(u => u.plano === 'PRO').length})
          </Button>
        </div>

        {/* Results count */}
        <p className="text-sm text-gray-600 mb-4">
          Mostrando {Math.min((usersPage - 1) * ITEMS_PER_PAGE + 1, filteredUsers.length)}–{Math.min(usersPage * ITEMS_PER_PAGE, filteredUsers.length)} de {filteredUsers.length} usuários
        </p>

        {/* Users Table */}
        <Card className="rounded-2xl border shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Empresa/Nome</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Profissao</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plano</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lembretes</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acoes</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{user.nome_empresa}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-600">{user.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.tipo_usuario === 'PF' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                      }`}>
                        {user.tipo_usuario || 'PJ'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-600">{user.tipo_profissao}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.plano === 'PRO' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {user.plano}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{user.contador_lembretes || 0}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.status === 'frozen' ? 'bg-blue-100 text-blue-800' :
                        user.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {user.status === 'frozen' ? 'Congelado' :
                         user.status === 'cancelled' ? 'Cancelado' :
                         'Ativo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          data-testid={`view-details-${user.id}`}
                          onClick={() => handleViewDetails(user)}
                          className="p-1.5 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors"
                          title="Ver detalhes"
                        >
                          <Eye className="w-4 h-4" />
                        </button>

                        {user.plano === 'FREE' ? (
                          <button
                            data-testid={`upgrade-pro-${user.id}`}
                            onClick={() => handleUpdatePlan(user.id, 'PRO')}
                            className="p-1.5 rounded-lg text-green-600 hover:bg-green-50 transition-colors"
                            title="Upgrade para PRO"
                          >
                            <TrendingUp className="w-4 h-4" />
                          </button>
                        ) : (
                          <button
                            data-testid={`downgrade-free-${user.id}`}
                            onClick={() => handleUpdatePlan(user.id, 'FREE')}
                            className="p-1.5 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
                            title="Downgrade para FREE"
                          >
                            <TrendingUp className="w-4 h-4 rotate-180" />
                          </button>
                        )}

                        {user.status !== 'frozen' ? (
                          <button
                            data-testid={`freeze-${user.id}`}
                            onClick={() => handleUpdateStatus(user.id, 'freeze')}
                            className="p-1.5 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors"
                            title="Congelar conta"
                          >
                            <Ban className="w-4 h-4" />
                          </button>
                        ) : (
                          <button
                            data-testid={`activate-${user.id}`}
                            onClick={() => handleUpdateStatus(user.id, 'activate')}
                            className="p-1.5 rounded-lg text-green-600 hover:bg-green-50 transition-colors"
                            title="Descongelar conta"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                        )}

                        <button
                          data-testid={`delete-user-${user.id}`}
                          onClick={() => handleDelete(user.id)}
                          className={`p-1.5 rounded-lg text-red-600 hover:bg-red-50 transition-colors ${!isAdmin ? 'hidden' : ''}`}
                          title="Deletar usuario"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Paginação Usuários */}
        {usersTotalPages > 1 && (
          <div className="flex items-center justify-between mt-4 px-2">
            <p className="text-sm text-gray-500">
              Página {usersPage} de {usersTotalPages}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setUsersPage(p => Math.max(1, p - 1))}
                disabled={usersPage === 1}
                className="px-3 py-1.5 rounded-lg border text-sm disabled:opacity-40 hover:bg-gray-50 transition-colors"
              >
                ← Anterior
              </button>
              {Array.from({ length: usersTotalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === usersTotalPages || Math.abs(p - usersPage) <= 1)
                .reduce((acc, p, i, arr) => {
                  if (i > 0 && p - arr[i-1] > 1) acc.push('...');
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, i) => p === '...' ? (
                  <span key={`dots-${i}`} className="px-2 py-1.5 text-sm text-gray-400">...</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => setUsersPage(p)}
                    className={`px-3 py-1.5 rounded-lg border text-sm transition-colors ${
                      usersPage === p ? 'bg-primary text-white border-primary' : 'hover:bg-gray-50'
                    }`}
                  >
                    {p}
                  </button>
                ))
              }
              <button
                onClick={() => setUsersPage(p => Math.min(usersTotalPages, p + 1))}
                disabled={usersPage === usersTotalPages}
                className="px-3 py-1.5 rounded-lg border text-sm disabled:opacity-40 hover:bg-gray-50 transition-colors"
              >
                Próxima →
              </button>
            </div>
          </div>
        )}
        </>
        )}

        {activeTab === 'profissoes' && (
        <div data-testid="profissoes-section">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Gerenciar Profissoes</h2>
              <p className="text-sm text-gray-500 mt-1">Defina as profissoes disponiveis e o tempo de retorno padrao de cada uma</p>
            </div>
            <Button
              data-testid="add-profissao-button"
              onClick={() => openProfForm()}
              className="rounded-xl bg-primary hover:bg-primary-hover text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nova Profissao
            </Button>
          </div>

          {profLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : (
            <div className="grid gap-3">
              {profissoes.map((prof) => (
                <Card key={prof.id} className="p-4 rounded-xl border shadow-sm hover:shadow-md transition-shadow" data-testid={`profissao-card-${prof.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
                        <Briefcase className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{prof.nome}</p>
                        <div className="flex items-center gap-1 mt-0.5">
                          <Clock className="w-3.5 h-3.5 text-gray-400" />
                          <span className="text-sm text-gray-500">
                            Retorno: {prof.tempo_retorno_dias} {prof.tempo_retorno_dias === 1 ? 'dia' : 'dias'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        data-testid={`edit-profissao-${prof.id}`}
                        onClick={() => openProfForm(prof)}
                        className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
                        title="Editar"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`delete-profissao-${prof.id}`}
                        onClick={() => deleteProfissao(prof)}
                        className="p-2 rounded-lg text-red-500 hover:bg-red-50 transition-colors"
                        title="Excluir"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </Card>
              ))}
              {profissoes.length === 0 && (
                <div className="text-center py-12 text-gray-400">
                  <Briefcase className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Nenhuma profissao cadastrada</p>
                </div>
              )}
            </div>
          )}
        </div>
        )}

        {/* ===== ABAS GENÉRICAS: Tipos Seguro, Seguradoras, Corretoras ===== */}
        {['tipos-seguro', 'seguradoras', 'corretoras'].includes(activeTab) && (
        <div data-testid={`${activeTab}-section`}>
          {(() => {
            const cfg = crudConfig[activeTab];
            const Icon = cfg.icon;
            return (
              <>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Gerenciar {cfg.label}s</h2>
                    <p className="text-sm text-gray-500 mt-1">Adicione, edite ou remova {cfg.label.toLowerCase()}s disponiveis no sistema</p>
                  </div>
                  <Button data-testid={`add-${activeTab}-button`} onClick={() => openCrudForm()} className="rounded-xl bg-primary hover:bg-primary-hover text-white">
                    <Plus className="w-4 h-4 mr-2" />
                    {cfg.label}
                  </Button>
                </div>

                {crudTab.loading ? (
                  <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
                ) : (
                  <div className="grid gap-3">
                    {crudTab.items.map((item) => (
                      <Card key={item.id} className="p-4 rounded-xl border shadow-sm hover:shadow-md transition-shadow" data-testid={`${activeTab}-card-${item.id}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
                              <Icon className="w-5 h-5 text-primary" />
                            </div>
                            <p className="font-medium text-gray-900">{item.nome}</p>
                          </div>
                          <div className="flex items-center gap-1">
                            <button data-testid={`edit-${activeTab}-${item.id}`} onClick={() => openCrudForm(item)} className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors" title="Editar">
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button data-testid={`delete-${activeTab}-${item.id}`} onClick={() => deleteCrudItem(item)} className="p-2 rounded-lg text-red-500 hover:bg-red-50 transition-colors" title="Excluir">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </Card>
                    ))}
                    {crudTab.items.length === 0 && (
                      <div className="text-center py-12 text-gray-400">
                        <Icon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>Nenhum registro cadastrado</p>
                      </div>
                    )}
                  </div>
                )}
              </>
            );
          })()}
        </div>
        )}

        {/* ==================== ABA SERVIÇOS ==================== */}
        {activeTab === 'servicos' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Serviços</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Serviços vinculados às profissões - exibidos para usuários PF na Timeline
                </p>
              </div>
              <Button
                data-testid="new-servico-button"
                onClick={() => openServicoModal()}
                className="rounded-xl bg-primary hover:bg-primary-hover text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Novo Serviço
              </Button>
            </div>

            {servicosLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : (
              <div className="space-y-3">
                {/* Agrupar por profissão */}
                {profissoes.map(prof => {
                  const servicosDaProf = servicos.filter(s => s.profissao_id === prof.id);
                  if (servicosDaProf.length === 0) return null;
                  
                  return (
                    <Card key={prof.id} className="rounded-xl border shadow-sm overflow-hidden">
                      <div className="bg-gray-50 px-4 py-3 border-b flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Briefcase className="w-4 h-4 text-gray-500" />
                          <span className="font-semibold text-gray-700">{prof.nome}</span>
                          <span className="text-xs text-gray-400">({servicosDaProf.length} serviços)</span>
                        </div>
                      </div>
                      <div className="divide-y">
                        {servicosDaProf.map(servico => (
                          <div key={servico.id} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50">
                            <div className="flex items-center gap-3">
                              <FileText className="w-4 h-4 text-primary" />
                              <span className="text-gray-900">{servico.nome}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <button
                                data-testid={`edit-servico-${servico.id}`}
                                onClick={() => openServicoModal(servico)}
                                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100"
                                title="Editar"
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                              <button
                                data-testid={`delete-servico-${servico.id}`}
                                onClick={() => deleteServico(servico)}
                                className="p-2 rounded-lg text-red-500 hover:bg-red-50"
                                title="Excluir"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </Card>
                  );
                })}

                {/* Serviços órfãos (se a profissão foi desativada) */}
                {servicos.filter(s => !profissoes.find(p => p.id === s.profissao_id)).length > 0 && (
                  <Card className="rounded-xl border border-yellow-200 shadow-sm overflow-hidden">
                    <div className="bg-yellow-50 px-4 py-3 border-b border-yellow-200">
                      <span className="font-semibold text-yellow-700">⚠️ Sem profissão vinculada</span>
                    </div>
                    <div className="divide-y">
                      {servicos.filter(s => !profissoes.find(p => p.id === s.profissao_id)).map(servico => (
                        <div key={servico.id} className="px-4 py-3 flex items-center justify-between hover:bg-yellow-50">
                          <span className="text-gray-900">{servico.nome}</span>
                          <div className="flex items-center gap-1">
                            <button onClick={() => openServicoModal(servico)} className="p-2 rounded-lg text-gray-500 hover:bg-gray-100">
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button onClick={() => deleteServico(servico)} className="p-2 rounded-lg text-red-500 hover:bg-red-50">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}

                {servicos.length === 0 && (
                  <div className="text-center py-12 text-gray-400">
                    <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Nenhum serviço cadastrado</p>
                    <p className="text-sm mt-1">Adicione serviços para cada profissão</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ==================== ABA LEMBRETES ==================== */}
        {activeTab === 'lembretes' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Lembretes</h2>
                <p className="text-gray-500 mt-1">Visão geral de todos os lembretes cadastrados</p>
              </div>
              <button
                onClick={fetchAdminLembretes}
                className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 text-sm hover:bg-gray-50 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Atualizar
              </button>
            </div>

            {/* Filtros */}
            <div className="flex flex-wrap gap-3 mb-6">
              <div className="flex gap-2 flex-wrap">
                {[
                  { id: 'todos', label: 'Todos' },
                  { id: 'free', label: 'Essencial' },
                  { id: 'pro', label: 'Profissional' },
                ].map(f => (
                  <button
                    key={f.id}
                    onClick={() => { setAdminLembretesFilter(f.id); setLembretesPage(1); }}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                      adminLembretesFilter === f.id
                        ? 'bg-primary text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
                <div className="w-px bg-gray-200 mx-1" />
                {[
                  { id: 'todos_origem', label: '📋 Todas origens' },
                  { id: 'PJ', label: '🏢 Manual (PJ)' },
                  { id: 'QR_CODE', label: '📱 QR Code' },
                  { id: 'PF', label: '👤 Timeline (PF)' },
                ].map(f => (
                  <button
                    key={f.id}
                    onClick={() => { setAdminLembretesOrigemFilter(f.id); setLembretesPage(1); }}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                      adminLembretesOrigemFilter === f.id
                        ? 'bg-gray-700 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <input
                type="text"
                placeholder="Buscar por empresa, cliente ou serviço..."
                value={adminLembretesSearch}
                onChange={e => { setAdminLembretesSearch(e.target.value); setLembretesPage(1); }}
                className="flex-1 min-w-[250px] px-4 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-primary"
              />
            </div>

            {/* Tabela */}
            {adminLembretesLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : (() => {
              const filtrados = adminLembretes.filter(l => {
                const matchPlano = adminLembretesFilter === 'todos' ||
                  (adminLembretesFilter === 'pro' && l.plano === 'PRO') ||
                  (adminLembretesFilter === 'free' && l.plano !== 'PRO');
                const matchOrigem = adminLembretesOrigemFilter === 'todos_origem' ||
                  l.origem === adminLembretesOrigemFilter;
                const q = adminLembretesSearch.toLowerCase();
                const matchSearch = !q ||
                  l.nome_empresa?.toLowerCase().includes(q) ||
                  l.cliente_nome?.toLowerCase().includes(q) ||
                  l.tipo_servico?.toLowerCase().includes(q);
                return matchPlano && matchOrigem && matchSearch;
              });

              const lemTotalPages = Math.ceil(filtrados.length / ITEMS_PER_PAGE);
              const paginatedLembretes = filtrados.slice((lembretesPage - 1) * ITEMS_PER_PAGE, lembretesPage * ITEMS_PER_PAGE);

              return (
                <>
                  <p className="text-sm text-gray-500 mb-3">
                    Mostrando {Math.min((lembretesPage - 1) * ITEMS_PER_PAGE + 1, filtrados.length)}–{Math.min(lembretesPage * ITEMS_PER_PAGE, filtrados.length)} de {filtrados.length} lembrete{filtrados.length !== 1 ? 's' : ''}
                  </p>
                  <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 border-b border-gray-200">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Empresa</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Serviço</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Renovação</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Origem</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Plano</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Notificações</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {filtrados.length === 0 ? (
                            <tr>
                              <td colSpan="7" className="px-4 py-8 text-center text-gray-400">
                                Nenhum lembrete encontrado
                              </td>
                            </tr>
                          ) : paginatedLembretes.map(l => {
                            const hoje = new Date();
                            const dtRen = l.data_renovacao ? new Date(l.data_renovacao) : null;
                            const dias = dtRen ? Math.ceil((dtRen - hoje) / (1000 * 60 * 60 * 24)) : null;
                            const vencido = dias !== null && dias < 0;

                            return (
                              <tr key={l.id} className="hover:bg-gray-50 transition-colors">
                                <td className="px-4 py-3">
                                  <p className="font-medium text-gray-900 text-xs">{l.nome_empresa || '—'}</p>
                                  <p className="text-xs text-gray-400">{l.tipo_profissao || ''}</p>
                                </td>
                                <td className="px-4 py-3">
                                  <p className="text-gray-900 text-xs">{l.cliente_nome || '—'}</p>
                                  <p className="text-xs text-gray-400">{l.cliente_email || ''}</p>
                                </td>
                                <td className="px-4 py-3 text-xs text-gray-700">{l.tipo_servico || '—'}</td>
                                <td className="px-4 py-3 text-xs">
                                  {dtRen ? (
                                    <span className={vencido ? 'text-red-600 font-medium' : 'text-gray-700'}>
                                      {dtRen.toLocaleDateString('pt-BR')}
                                      {dias !== null && (
                                        <span className="block text-xs text-gray-400">
                                          {vencido ? `${Math.abs(dias)}d atrás` : `em ${dias}d`}
                                        </span>
                                      )}
                                    </span>
                                  ) : '—'}
                                </td>
                                <td className="px-4 py-3 text-center">
                                  {l.origem === 'QR_CODE' ? (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700" title="Cliente escaneou QR Code">
                                      📱 QR Code
                                    </span>
                                  ) : l.origem === 'PF' ? (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700" title="Cliente criou na Timeline">
                                      👤 Timeline
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600" title="Profissional criou manualmente">
                                      🏢 Manual
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                                    l.plano === 'PRO'
                                      ? 'bg-purple-100 text-purple-700'
                                      : 'bg-gray-100 text-gray-600'
                                  }`}>
                                    {l.plano === 'PRO' ? 'PRO' : 'FREE'}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                                    l.status_lembrete === 'ativo'
                                      ? 'bg-green-100 text-green-700'
                                      : l.status_lembrete === 'renovado'
                                      ? 'bg-blue-100 text-blue-700'
                                      : 'bg-red-100 text-red-700'
                                  }`}>
                                    {l.status_lembrete || 'ativo'}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <div className="flex items-center justify-center gap-1 text-xs">
                                    <span title="Email D-30" className={l.notif_30d_sent ? 'text-green-500' : 'text-gray-300'}>📧30</span>
                                    <span title="Email D-15" className={l.notif_15d_sent ? 'text-green-500' : 'text-gray-300'}>📧15</span>
                                    <span title="Email D-7" className={l.notif_7d_sent ? 'text-green-500' : 'text-gray-300'}>📧7</span>
                                    <span title="Email D-0" className={l.notif_0d_sent ? 'text-green-500' : 'text-gray-300'}>📧0</span>
                                    <span className="text-gray-200 mx-1">|</span>
                                    <span title="WhatsApp D-30" className={l.notif_30d_whatsapp_sent ? 'text-green-500' : 'text-gray-300'}>📱30</span>
                                    <span title="WhatsApp D-15" className={l.notif_15d_whatsapp_sent ? 'text-green-500' : 'text-gray-300'}>📱15</span>
                                    <span title="WhatsApp D-7" className={l.notif_7d_whatsapp_sent ? 'text-green-500' : 'text-gray-300'}>📱7</span>
                                    <span title="WhatsApp D-0" className={l.notif_0d_whatsapp_sent ? 'text-green-500' : 'text-gray-300'}>📱0</span>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Paginação Lembretes */}
                  {lemTotalPages > 1 && (
                    <div className="flex items-center justify-between mt-4 px-2">
                      <p className="text-sm text-gray-500">
                        Página {lembretesPage} de {lemTotalPages}
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setLembretesPage(p => Math.max(1, p - 1))}
                          disabled={lembretesPage === 1}
                          className="px-3 py-1.5 rounded-lg border text-sm disabled:opacity-40 hover:bg-gray-50 transition-colors"
                        >
                          ← Anterior
                        </button>
                        {Array.from({ length: lemTotalPages }, (_, i) => i + 1)
                          .filter(p => p === 1 || p === lemTotalPages || Math.abs(p - lembretesPage) <= 1)
                          .reduce((acc, p, i, arr) => {
                            if (i > 0 && p - arr[i-1] > 1) acc.push('...');
                            acc.push(p);
                            return acc;
                          }, [])
                          .map((p, i) => p === '...' ? (
                            <span key={`dots-${i}`} className="px-2 py-1.5 text-sm text-gray-400">...</span>
                          ) : (
                            <button
                              key={p}
                              onClick={() => setLembretesPage(p)}
                              className={`px-3 py-1.5 rounded-lg border text-sm transition-colors ${
                                lembretesPage === p ? 'bg-primary text-white border-primary' : 'hover:bg-gray-50'
                              }`}
                            >
                              {p}
                            </button>
                          ))
                        }
                        <button
                          onClick={() => setLembretesPage(p => Math.min(lemTotalPages, p + 1))}
                          disabled={lembretesPage === lemTotalPages}
                          className="px-3 py-1.5 rounded-lg border text-sm disabled:opacity-40 hover:bg-gray-50 transition-colors"
                        >
                          Próxima →
                        </button>
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {/* ==================== ABA CUPONS ==================== */}
        {activeTab === 'cupons' && (
          <div>
            {/* Header com stats */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Cupons de Desconto</h2>
                <p className="text-sm text-gray-500 mt-1">
                  {cupons.filter(c => c.ativo).length} cupons ativos · {cupons.reduce((acc, c) => acc + (c.usos_realizados || 0), 0)} usos totais
                </p>
              </div>
              <Button
                data-testid="new-cupom-button"
                onClick={() => openCupomForm()}
                className="rounded-xl bg-primary hover:bg-primary-hover text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Novo Cupom
              </Button>
            </div>

            {/* Lista de Cupons */}
            <Card className="rounded-2xl border shadow-sm overflow-hidden">
              {cuponsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : cupons.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <Ticket className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Nenhum cupom cadastrado</p>
                  <p className="text-sm mt-1">Crie o primeiro cupom de desconto</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Desconto</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duração</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Validade</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usos</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ações</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {cupons.map((cupom) => {
                        const hoje = new Date().toISOString().split('T')[0];
                        const expirado = cupom.validade_fim && cupom.validade_fim < hoje;
                        const naoIniciado = cupom.validade_inicio && cupom.validade_inicio > hoje;
                        const limiteAtingido = cupom.limite_usos && cupom.usos_realizados >= cupom.limite_usos;
                        
                        return (
                          <tr key={cupom.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <code className="px-2 py-1 bg-gray-100 rounded text-sm font-mono font-bold text-gray-900">
                                  {cupom.codigo}
                                </code>
                                {cupom.descricao && (
                                  <span className="text-xs text-gray-400" title={cupom.descricao}>
                                    ℹ️
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-semibold ${
                                cupom.desconto_percent === 100 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-blue-100 text-blue-800'
                              }`}>
                                {cupom.desconto_percent}% OFF
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                cupom.duracao_meses === null || cupom.duracao_meses === undefined
                                  ? 'bg-purple-100 text-purple-800'
                                  : 'bg-blue-100 text-blue-800'
                              }`}>
                                {cupom.duracao_texto || cupom.duracao_meses ? `${cupom.duracao_meses} ${cupom.duracao_meses === 1 ? 'mês' : 'meses'}` : 'Permanente'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                              {cupom.validade_inicio || cupom.validade_fim ? (
                                <div className="flex flex-col">
                                  {cupom.validade_inicio && (
                                    <span>De: {new Date(cupom.validade_inicio).toLocaleDateString('pt-BR')}</span>
                                  )}
                                  {cupom.validade_fim && (
                                    <span>Até: {new Date(cupom.validade_fim).toLocaleDateString('pt-BR')}</span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-gray-400">Sem limite</span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {cupom.usos_realizados > 0 ? (
                                <button
                                  onClick={() => viewCupomUsos(cupom)}
                                  className="inline-flex items-center gap-1.5 text-sm text-primary hover:text-primary-hover font-medium transition-colors"
                                  title="Ver quem usou"
                                >
                                  <Eye className="w-4 h-4" />
                                  {cupom.usos_realizados}
                                  {cupom.limite_usos && (
                                    <span className="text-gray-400"> / {cupom.limite_usos}</span>
                                  )}
                                </button>
                              ) : (
                                <span className="text-sm text-gray-400">
                                  0
                                  {cupom.limite_usos && (
                                    <span> / {cupom.limite_usos}</span>
                                  )}
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {!cupom.ativo ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                  Inativo
                                </span>
                              ) : expirado ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                                  Expirado
                                </span>
                              ) : naoIniciado ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                                  Agendado
                                </span>
                              ) : limiteAtingido ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                                  Esgotado
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                  Ativo
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={() => openCupomForm(cupom)}
                                  className="text-primary hover:text-primary-hover"
                                  title="Editar"
                                >
                                  <Pencil className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => toggleCupomStatus(cupom)}
                                  className={cupom.ativo ? 'text-yellow-600 hover:text-yellow-700' : 'text-green-600 hover:text-green-700'}
                                  title={cupom.ativo ? 'Desativar' : 'Ativar'}
                                >
                                  {cupom.ativo ? <Ban className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                                </button>
                                <button
                                  onClick={() => deleteCupom(cupom)}
                                  className="text-red-600 hover:text-red-700"
                                  title="Excluir"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>
        )}

        {/* ==================== ABA CNAE-CUPONS ==================== */}
        {activeTab === 'cnae-cupons' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">CNAE → Cupom</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Associe códigos CNAE a cupons de desconto automático no cadastro
                </p>
              </div>
              <Button
                onClick={() => {
                  setCnaeForm({ cnae: '', descricao_cnae: '', cupom_id: '' });
                  setShowCnaeModal(true);
                }}
                className="rounded-xl bg-primary hover:bg-primary-hover text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Novo CNAE
              </Button>
            </div>

            {/* Info Box */}
            <Card className="p-4 bg-blue-50 border-blue-200 rounded-xl">
              <div className="flex items-start gap-3">
                <Gift className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-800">Como funciona?</p>
                  <p className="text-sm text-blue-700 mt-1">
                    Quando alguém cadastra uma empresa (PJ), consultamos o CNPJ na Receita Federal. 
                    Se o CNAE da empresa estiver associado a um cupom aqui, o desconto é aplicado automaticamente!
                  </p>
                </div>
              </div>
            </Card>

            {/* Lista */}
            <Card className="p-6 rounded-2xl border shadow-sm">
              {cnaeCuponsLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : cnaeCupons.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  Nenhum CNAE associado ainda
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">CNAE</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Descrição</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Cupom</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Desconto</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-gray-600">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cnaeCupons.map((item) => (
                        <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                              {item.cnae}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600">
                            {item.descricao_cnae || '-'}
                          </td>
                          <td className="py-3 px-4">
                            <span className="font-mono text-sm font-medium text-primary">
                              {item.codigo}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="bg-green-100 text-green-700 text-xs font-semibold px-2 py-1 rounded">
                              {item.desconto_percent}% OFF
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <button
                              onClick={() => deleteCnaeCupom(item.id, item.cnae)}
                              className="text-red-600 hover:text-red-700"
                              title="Remover"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>
        )}

        {/* Modal Novo CNAE */}
        {showCnaeModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md p-6 rounded-2xl">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Gift className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold">Novo CNAE → Cupom</h3>
                </div>
                <button onClick={() => setShowCnaeModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Código CNAE <span className="text-red-500">*</span>
                  </label>
                  <Input
                    placeholder="Ex: 6622300"
                    value={cnaeForm.cnae}
                    onChange={(e) => setCnaeForm({ ...cnaeForm, cnae: e.target.value.replace(/\D/g, '') })}
                    className="rounded-xl font-mono"
                    maxLength={7}
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    7 dígitos completos (ex: 66223<b>00</b>). Consulte em <a href="https://concla.ibge.gov.br/busca-online-cnae.html" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">ibge.gov.br</a>
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descrição da Atividade
                  </label>
                  <Input
                    placeholder="Ex: Cabeleireiros, manicure e pedicure"
                    value={cnaeForm.descricao_cnae}
                    onChange={(e) => setCnaeForm({ ...cnaeForm, descricao_cnae: e.target.value })}
                    className="rounded-xl"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cupom Associado <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={cnaeForm.cupom_id}
                    onChange={(e) => setCnaeForm({ ...cnaeForm, cupom_id: e.target.value })}
                    className="w-full h-11 px-3 rounded-xl border border-gray-200 focus:border-primary focus:ring-2 focus:ring-red-100 outline-none"
                  >
                    <option value="">Selecione um cupom...</option>
                    {cupons.filter(c => c.ativo).map(c => (
                      <option key={c.id} value={c.id}>
                        {c.codigo} ({c.desconto_percent}% OFF)
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <Button
                  variant="outline"
                  onClick={() => setShowCnaeModal(false)}
                  className="flex-1 rounded-xl"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={saveCnaeCupom}
                  className="flex-1 rounded-xl bg-primary hover:bg-primary-hover text-white"
                >
                  Salvar
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* ==================== ABA MONITORAMENTO ==================== */}
        {activeTab === 'monitoramento' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Monitoramento de Notificações</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Acompanhe os envios de email e WhatsApp
                </p>
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => { fetchMonitoramento(); fetchHistoricoNotificacoes(); }}
                  disabled={monitoramentoLoading}
                  className="rounded-xl"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${monitoramentoLoading ? 'animate-spin' : ''}`} />
                  Atualizar
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowTesteModal(true)}
                  className="rounded-xl"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Testar Envio
                </Button>
                <Button
                  onClick={() => dispararCron(null)}
                  disabled={cronDisparando}
                  className="rounded-xl bg-primary hover:bg-primary-hover text-white"
                >
                  {cronDisparando ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Play className="w-4 h-4 mr-2" />
                  )}
                  Disparar Lembretes
                </Button>
              </div>
            </div>

            {monitoramentoLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : monitoramento ? (
              <>
                {/* Cards de Status */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {/* WhatsApp Status */}
                  <Card className="p-4 md:p-6 rounded-2xl border shadow-sm">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs md:text-sm text-gray-500 mb-1">WhatsApp</p>
                        <p className={`text-lg md:text-xl font-bold ${monitoramento.whatsapp?.conexao?.connected ? 'text-green-600' : 'text-red-600'}`}>
                          {monitoramento.whatsapp?.conexao?.connected ? 'Conectado' : 'Desconectado'}
                        </p>
                        <p className="text-xs text-gray-400">{monitoramento.whatsapp?.provider || 'N/A'}</p>
                      </div>
                      <div className={`w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center ${monitoramento.whatsapp?.conexao?.connected ? 'bg-green-100' : 'bg-red-100'}`}>
                        <MessageCircle className={`w-5 h-5 md:w-6 md:h-6 ${monitoramento.whatsapp?.conexao?.connected ? 'text-green-600' : 'text-red-600'}`} />
                      </div>
                    </div>
                  </Card>

                  {/* Emails 24h */}
                  <Card className="p-4 md:p-6 rounded-2xl border shadow-sm">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs md:text-sm text-gray-500 mb-1">Emails (24h)</p>
                        <p className="text-2xl md:text-3xl font-bold text-blue-600">
                          {monitoramento.estatisticas_24h?.emails_enviados || 0}
                        </p>
                      </div>
                      <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                        <Mail className="w-5 h-5 md:w-6 md:h-6 text-blue-600" />
                      </div>
                    </div>
                  </Card>

                  {/* WhatsApps 24h */}
                  <Card className="p-4 md:p-6 rounded-2xl border shadow-sm">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs md:text-sm text-gray-500 mb-1">WhatsApps (24h)</p>
                        <p className="text-2xl md:text-3xl font-bold text-green-600">
                          {monitoramento.estatisticas_24h?.whatsapp_enviados || 0}
                        </p>
                      </div>
                      <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-green-100 flex items-center justify-center">
                        <MessageCircle className="w-5 h-5 md:w-6 md:h-6 text-green-600" />
                      </div>
                    </div>
                  </Card>

                  {/* Erros 24h */}
                  <Card className="p-4 md:p-6 rounded-2xl border shadow-sm">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs md:text-sm text-gray-500 mb-1">Erros (24h)</p>
                        <p className={`text-2xl md:text-3xl font-bold ${(monitoramento.estatisticas_24h?.erros || 0) > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                          {monitoramento.estatisticas_24h?.erros || 0}
                        </p>
                      </div>
                      <div className={`w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center ${(monitoramento.estatisticas_24h?.erros || 0) > 0 ? 'bg-red-100' : 'bg-gray-100'}`}>
                        <AlertTriangle className={`w-5 h-5 md:w-6 md:h-6 ${(monitoramento.estatisticas_24h?.erros || 0) > 0 ? 'text-red-600' : 'text-gray-400'}`} />
                      </div>
                    </div>
                  </Card>
                </div>

                {/* Disparos Pendentes */}
                <Card className="rounded-2xl border shadow-sm p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Disparos Pendentes</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {Object.entries(monitoramento.disparos_pendentes || {}).map(([gatilho, info]) => (
                      <div key={gatilho} className="bg-gray-50 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-700">{gatilho}</span>
                          <span className="text-xs text-gray-400">{formatarDataBR(info.data)}</span>
                        </div>
                        <div className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-500">Total lembretes:</span>
                            <span className="font-medium">{info.total_lembretes}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-500">Emails pendentes:</span>
                            <span className={`font-medium ${info.pendentes_email > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                              {info.pendentes_email}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-500">WhatsApps pendentes:</span>
                            <span className={`font-medium ${info.pendentes_whatsapp > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                              {info.pendentes_whatsapp}
                            </span>
                          </div>
                        </div>
                        {(info.pendentes_email > 0 || info.pendentes_whatsapp > 0) && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => dispararCron(parseInt(gatilho.replace('D-', '')))}
                            className="w-full mt-3 rounded-lg text-xs"
                          >
                            Disparar {gatilho}
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </Card>

                {/* Histórico de Envios */}
                <Card className="rounded-2xl border shadow-sm overflow-hidden">
                  <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">Histórico de Envios</h3>
                    <div className="flex gap-2">
                      <select
                        value={historicoFiltro.canal}
                        onChange={(e) => { setHistoricoFiltro(prev => ({ ...prev, canal: e.target.value })); }}
                        className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm"
                      >
                        <option value="">Todos os canais</option>
                        <option value="email">Email</option>
                        <option value="whatsapp">WhatsApp</option>
                      </select>
                      <select
                        value={historicoFiltro.status}
                        onChange={(e) => { setHistoricoFiltro(prev => ({ ...prev, status: e.target.value })); }}
                        className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm"
                      >
                        <option value="">Todos os status</option>
                        <option value="sent">Enviado</option>
                        <option value="error">Erro</option>
                      </select>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={fetchHistoricoNotificacoes}
                        className="rounded-lg"
                      >
                        Filtrar
                      </Button>
                    </div>
                  </div>
                  
                  {historicoLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                    </div>
                  ) : historicoNotificacoes.length === 0 ? (
                    <div className="text-center py-12 text-gray-400">
                      <Bell className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Nenhuma notificação encontrada</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data/Hora</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Canal</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Serviço</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Empresa</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {historicoNotificacoes.map((notif, idx) => (
                            <tr key={notif.id || idx} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {formatarDataHora(notif.enviado_em)}
                              </td>
                              <td className="px-4 py-3">
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                                  notif.canal === 'email' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                                }`}>
                                  {notif.canal === 'email' ? <Mail className="w-3 h-3" /> : <MessageCircle className="w-3 h-3" />}
                                  {notif.canal}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {notif.tipo}
                              </td>
                              <td className="px-4 py-3">
                                <div className="text-sm font-medium text-gray-900">{notif.cliente_nome}</div>
                                <div className="text-xs text-gray-400">
                                  {notif.canal === 'email' ? notif.email_para || notif.cliente_email : notif.whatsapp_para || notif.cliente_telefone}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {notif.tipo_servico}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {notif.nome_empresa}
                              </td>
                              <td className="px-4 py-3 text-center">
                                {notif.status === 'sent' ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                    <CheckCircle2 className="w-3 h-3" />
                                    Enviado
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                                    <XCircle className="w-3 h-3" />
                                    Erro
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>
              </>
            ) : (
              <div className="text-center py-12 text-gray-400">
                <AlertTriangle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Erro ao carregar dados de monitoramento</p>
                <Button variant="outline" onClick={fetchMonitoramento} className="mt-4 rounded-xl">
                  Tentar novamente
                </Button>
              </div>
            )}
          </div>
        )}

        {/* ==================== ABA TEMPLATES ==================== */}
        {activeTab === 'templates' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Templates de Mensagem</h2>
                <p className="text-sm text-gray-500 mt-1">Personalize as mensagens por tipo de profissão</p>
              </div>
              <Button
                onClick={() => openTemplateForm()}
                className="rounded-xl bg-primary hover:bg-primary-hover"
              >
                <Plus className="w-4 h-4 mr-2" />
                Novo Template
              </Button>
            </div>

            {/* Filtros */}
            <Card className="p-4 rounded-2xl">
              <div className="flex flex-wrap gap-4 items-end">
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Profissão</label>
                  <select
                    value={templateFiltro.profissao}
                    onChange={(e) => setTemplateFiltro(prev => ({ ...prev, profissao: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-xl focus:ring-2 focus:ring-primary focus:border-primary"
                  >
                    <option value="">Todas</option>
                    {templatesConfig.profissoes.map(p => (
                      <option key={p.id} value={p.id}>{p.nome}</option>
                    ))}
                  </select>
                </div>
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Mensagem</label>
                  <select
                    value={templateFiltro.tipo}
                    onChange={(e) => setTemplateFiltro(prev => ({ ...prev, tipo: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-xl focus:ring-2 focus:ring-primary focus:border-primary"
                  >
                    <option value="">Todos</option>
                    {templatesConfig.tipos_mensagem.map(t => (
                      <option key={t.id} value={t.id}>{t.nome}</option>
                    ))}
                  </select>
                </div>
                <Button
                  variant="outline"
                  onClick={fetchTemplates}
                  className="rounded-xl"
                >
                  <Search className="w-4 h-4 mr-2" />
                  Filtrar
                </Button>
              </div>
            </Card>

            {/* Lista de Templates */}
            <Card className="rounded-2xl overflow-hidden">
              {templatesLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : templates.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Nenhum template encontrado</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Profissão</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Tipo</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Canal</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Preview</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Status</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Ações</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {templates.map(template => (
                        <tr key={template.id} className={`hover:bg-gray-50 ${template.ativo !== 1 ? 'opacity-50' : ''}`}>
                          <td className="px-4 py-3">
                            <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                              template.tipo_profissao === 'padrao' 
                                ? 'bg-gray-100 text-gray-700' 
                                : 'bg-blue-100 text-blue-700'
                            }`}>
                              {getProfissaoNome(template.tipo_profissao)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {getTipoMensagemNome(template.tipo_mensagem)}
                          </td>
                          <td className="px-4 py-3">
                            {getTipoMensagemCanal(template.tipo_mensagem) === 'whatsapp' ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                <MessageCircle className="w-3 h-3" />
                                WhatsApp
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                                <Mail className="w-3 h-3" />
                                Email
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-sm text-gray-600 max-w-xs truncate">
                              {template.conteudo.substring(0, 80)}...
                            </p>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => toggleTemplate(template)}
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer ${
                                template.ativo === 1 
                                  ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                              }`}
                            >
                              {template.ativo === 1 ? (
                                <><CheckCircle className="w-3 h-3" /> Ativo</>
                              ) : (
                                <><Ban className="w-3 h-3" /> Inativo</>
                              )}
                            </button>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex gap-2 justify-end">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openTemplateForm(template)}
                                className="rounded-lg"
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              {template.tipo_profissao !== 'padrao' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => deleteTemplate(template)}
                                  className="rounded-lg text-red-500 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            {/* Variáveis Disponíveis */}
            <Card className="p-4 rounded-2xl bg-blue-50 border-blue-200">
              <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Variáveis Disponíveis
              </h4>
              <div className="flex flex-wrap gap-2">
                {templatesConfig.variaveis.map(v => (
                  <span key={v.var} className="inline-flex items-center gap-1 px-2 py-1 bg-white rounded-lg text-xs border border-blue-200">
                    <code className="font-mono text-blue-600">{v.var}</code>
                    <span className="text-gray-500">— {v.desc}</span>
                  </span>
                ))}
              </div>
            </Card>
          </div>
        )}

        {/* Modal Template */}
        {showTemplateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-900">
                  {editingTemplate ? 'Editar Template' : 'Novo Template'}
                </h3>
                <button onClick={() => setShowTemplateModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Profissão *</label>
                  <select
                    value={templateForm.tipo_profissao}
                    onChange={(e) => setTemplateForm(prev => ({ ...prev, tipo_profissao: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-xl focus:ring-2 focus:ring-primary focus:border-primary"
                    disabled={!!editingTemplate}
                  >
                    <option value="">Selecione...</option>
                    {templatesConfig.profissoes.map(p => (
                      <option key={p.id} value={p.id}>{p.nome}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Mensagem *</label>
                  <select
                    value={templateForm.tipo_mensagem}
                    onChange={(e) => setTemplateForm(prev => ({ ...prev, tipo_mensagem: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-xl focus:ring-2 focus:ring-primary focus:border-primary"
                    disabled={!!editingTemplate}
                  >
                    <option value="">Selecione...</option>
                    {templatesConfig.tipos_mensagem.map(t => (
                      <option key={t.id} value={t.id}>{t.nome}</option>
                    ))}
                  </select>
                </div>
              </div>

              {templateForm.tipo_mensagem.startsWith('email_') && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Assunto do Email *</label>
                  <Input
                    value={templateForm.assunto}
                    onChange={(e) => setTemplateForm(prev => ({ ...prev, assunto: e.target.value }))}
                    placeholder="Ex: Lembrete: Sua manicure vence em {dias} dias"
                    className="rounded-xl"
                  />
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Conteúdo da Mensagem *</label>
                <textarea
                  value={templateForm.conteudo}
                  onChange={(e) => {
                    setTemplateForm(prev => ({ ...prev, conteudo: e.target.value }));
                    previewTemplate(e.target.value);
                  }}
                  rows={8}
                  placeholder="Digite o template da mensagem usando as variáveis disponíveis..."
                  className="w-full px-3 py-2 border rounded-xl focus:ring-2 focus:ring-primary focus:border-primary font-mono text-sm"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Use *texto* para negrito no WhatsApp. Para email, use HTML.
                </p>
              </div>

              {/* Preview */}
              {templatePreview && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Preview</label>
                  <div className={`p-4 rounded-xl border ${
                    templateForm.tipo_mensagem.startsWith('email_') 
                      ? 'bg-white' 
                      : 'bg-green-50 border-green-200'
                  }`}>
                    <pre className="text-sm whitespace-pre-wrap font-sans">
                      {templatePreview}
                    </pre>
                  </div>
                </div>
              )}

              {/* Variáveis rápidas */}
              <div className="mb-6 p-3 bg-gray-50 rounded-xl">
                <p className="text-xs text-gray-500 mb-2">Clique para inserir:</p>
                <div className="flex flex-wrap gap-1">
                  {templatesConfig.variaveis.map(v => (
                    <button
                      key={v.var}
                      type="button"
                      onClick={() => {
                        const textarea = document.querySelector('textarea');
                        const start = textarea.selectionStart;
                        const end = textarea.selectionEnd;
                        const newValue = templateForm.conteudo.substring(0, start) + v.var + templateForm.conteudo.substring(end);
                        setTemplateForm(prev => ({ ...prev, conteudo: newValue }));
                        previewTemplate(newValue);
                      }}
                      className="px-2 py-1 bg-white border rounded text-xs font-mono hover:bg-blue-50 hover:border-blue-300"
                    >
                      {v.var}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-4 mb-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={templateForm.ativo}
                    onChange={(e) => setTemplateForm(prev => ({ ...prev, ativo: e.target.checked }))}
                    className="w-4 h-4 text-primary rounded border-gray-300 focus:ring-primary"
                  />
                  <span className="text-sm text-gray-700">Template ativo</span>
                </label>
              </div>

              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setShowTemplateModal(false)} className="rounded-xl">
                  Cancelar
                </Button>
                <Button onClick={saveTemplate} className="rounded-xl bg-primary hover:bg-primary-hover">
                  {editingTemplate ? 'Salvar Alterações' : 'Criar Template'}
                </Button>
              </div>
            </Card>
          </div>
        )}

      </main>

      {/* Confirmation Modal (replaces window.confirm) */}
      <AlertDialog open={confirmModal.open} onOpenChange={(open) => { if (!open) closeConfirm(); }}>
        <AlertDialogContent className="rounded-2xl max-w-md" data-testid="confirm-modal">
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-1">
              {getConfirmIcon()}
              <AlertDialogTitle className="text-xl font-bold">
                {confirmModal.title}
              </AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-sm text-gray-600 whitespace-pre-line">
              {confirmModal.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 mt-2">
            <AlertDialogCancel
              data-testid="confirm-cancel-button"
              disabled={confirmModal.loading}
              className="rounded-xl"
              onClick={closeConfirm}
            >
              Cancelar
            </AlertDialogCancel>
            <Button
              data-testid="confirm-action-button"
              disabled={confirmModal.loading}
              className={`rounded-xl ${getActionButtonClass()}`}
              onClick={executeConfirm}
            >
              {confirmModal.loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processando...
                </>
              ) : (
                confirmModal.actionLabel
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ==================== ABA COLABORADORES ==================== */}
      {activeTab === 'colaboradores' && isAdmin && (
        <ColaboradoresTab API={API} />
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="max-w-2xl w-full rounded-3xl p-8 relative max-h-[90vh] overflow-y-auto" data-testid="user-details-modal">
            <button
              data-testid="close-details-modal"
              onClick={() => setShowDetailsModal(false)}
              className="absolute top-6 right-6 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>

            <h2 className="text-2xl font-bold text-gray-900 mb-6">Detalhes do Usuario</h2>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Nome/Empresa</p>
                  <p className="text-base font-medium text-gray-900">{selectedUser.nome_empresa}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="text-base font-medium text-gray-900">{selectedUser.email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Tipo</p>
                  <p className="text-base font-medium text-gray-900">{selectedUser.tipo_usuario || 'PJ'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Profissao</p>
                  <p className="text-base font-medium text-gray-900">{selectedUser.tipo_profissao}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Telefone</p>
                  <p className="text-base font-medium text-gray-900">{selectedUser.telefone || 'Nao informado'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">CNPJ</p>
                  <p className="text-base font-medium text-gray-900">{selectedUser.cnpj || 'Nao informado'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Plano</p>
                  <p className="text-base font-medium text-gray-900">{selectedUser.plano}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total de Lembretes</p>
                  <p className="text-base font-medium text-gray-900">{selectedUser.contador_lembretes || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <p className="text-base font-medium text-gray-900">
                    {selectedUser.status === 'frozen' ? 'Congelado' : selectedUser.status === 'cancelled' ? 'Cancelado' : 'Ativo'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Cadastrado em</p>
                  <p className="text-base font-medium text-gray-900">
                    {selectedUser.created_at ? new Date(selectedUser.created_at).toLocaleDateString('pt-BR') : 'N/A'}
                  </p>
                </div>
              </div>

              <div className="pt-4 border-t border-gray-200">
                <p className="text-sm font-medium text-gray-700 mb-3">Acoes Rapidas</p>
                <div className="flex gap-3 flex-wrap">
                  {selectedUser.plano === 'FREE' ? (
                    <Button
                      data-testid="modal-upgrade-button"
                      onClick={() => {
                        setShowDetailsModal(false);
                        handleUpdatePlan(selectedUser.id, 'PRO');
                      }}
                      size="sm"
                      className="rounded-xl bg-green-600 hover:bg-green-700 text-white"
                    >
                      <Crown className="w-4 h-4 mr-1" />
                      Upgrade PRO
                    </Button>
                  ) : (
                    <Button
                      data-testid="modal-downgrade-button"
                      onClick={() => {
                        setShowDetailsModal(false);
                        handleUpdatePlan(selectedUser.id, 'FREE');
                      }}
                      variant="outline"
                      size="sm"
                      className="rounded-xl"
                    >
                      Downgrade FREE
                    </Button>
                  )}
                  {selectedUser.status !== 'frozen' ? (
                    <Button
                      data-testid="modal-freeze-button"
                      onClick={() => {
                        setShowDetailsModal(false);
                        handleUpdateStatus(selectedUser.id, 'freeze');
                      }}
                      variant="outline"
                      size="sm"
                      className="rounded-xl text-blue-600 hover:text-blue-700"
                    >
                      <Ban className="w-4 h-4 mr-1" />
                      Congelar
                    </Button>
                  ) : (
                    <Button
                      data-testid="modal-activate-button"
                      onClick={() => {
                        setShowDetailsModal(false);
                        handleUpdateStatus(selectedUser.id, 'activate');
                      }}
                      size="sm"
                      className="rounded-xl bg-green-600 hover:bg-green-700 text-white"
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Descongelar
                    </Button>
                  )}
                  <Button
                    data-testid="modal-delete-button"
                    onClick={() => {
                      setShowDetailsModal(false);
                      handleDelete(selectedUser.id);
                    }}
                    variant="outline"
                    size="sm"
                    className="rounded-xl text-red-600 hover:text-red-700 border-red-200 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Excluir
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Profissão Create/Edit Modal */}
      {showProfModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="max-w-md w-full rounded-2xl p-6 relative" data-testid="profissao-modal">
            <button
              data-testid="close-profissao-modal"
              onClick={() => setShowProfModal(false)}
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
            >
              <X className="w-4 h-4 text-gray-600" />
            </button>

            <h2 className="text-xl font-bold text-gray-900 mb-6">
              {editingProf ? 'Editar Profissao' : 'Nova Profissao'}
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Nome da Profissao</label>
                <Input
                  data-testid="profissao-nome-input"
                  value={profForm.nome}
                  onChange={(e) => setProfForm({ ...profForm, nome: e.target.value })}
                  placeholder="Ex: Fisioterapeuta"
                  className="rounded-xl"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Tempo de Retorno (dias)</label>
                <Input
                  data-testid="profissao-tempo-input"
                  type="number"
                  min="1"
                  value={profForm.tempo_retorno_dias}
                  onChange={(e) => setProfForm({ ...profForm, tempo_retorno_dias: parseInt(e.target.value) || 1 })}
                  placeholder="365"
                  className="rounded-xl"
                />
                <p className="text-xs text-gray-400 mt-1.5">
                  Prazo padrao em dias para renovacao dos lembretes desta profissao
                </p>
              </div>
              <div className="flex gap-3 pt-2">
                <Button
                  data-testid="profissao-cancel-button"
                  variant="outline"
                  className="flex-1 rounded-xl"
                  onClick={() => setShowProfModal(false)}
                >
                  Cancelar
                </Button>
                <Button
                  data-testid="profissao-save-button"
                  className="flex-1 rounded-xl bg-primary hover:bg-primary-hover text-white"
                  onClick={saveProfissao}
                >
                  {editingProf ? 'Salvar' : 'Criar'}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Generic CRUD Modal for Seguros tables */}
      {crudTab.showModal && crudConfig[activeTab] && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="max-w-md w-full rounded-2xl p-6 relative" data-testid="crud-modal">
            <button data-testid="close-crud-modal" onClick={() => setCrudTab(prev => ({ ...prev, showModal: false }))} className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center">
              <X className="w-4 h-4 text-gray-600" />
            </button>
            <h2 className="text-xl font-bold text-gray-900 mb-6">
              {crudTab.editing ? `Editar ${crudConfig[activeTab].label}` : `Nova ${crudConfig[activeTab].label}`}
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Nome</label>
                <Input data-testid="crud-nome-input" value={crudTab.form.nome} onChange={(e) => setCrudTab(prev => ({ ...prev, form: { ...prev.form, nome: e.target.value } }))} placeholder={`Ex: Nome da ${crudConfig[activeTab].label.toLowerCase()}`} className="rounded-xl" />
              </div>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" className="flex-1 rounded-xl" onClick={() => setCrudTab(prev => ({ ...prev, showModal: false }))}>Cancelar</Button>
                <Button data-testid="crud-save-button" className="flex-1 rounded-xl bg-primary hover:bg-primary-hover text-white" onClick={saveCrudItem}>
                  {crudTab.editing ? 'Salvar' : 'Criar'}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Cupom Create/Edit Modal */}
      {showCupomModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="max-w-lg w-full rounded-2xl p-6 relative max-h-[90vh] overflow-y-auto" data-testid="cupom-modal">
            <button
              data-testid="close-cupom-modal"
              onClick={() => setShowCupomModal(false)}
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
            >
              <X className="w-4 h-4 text-gray-600" />
            </button>

            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Ticket className="w-5 h-5 text-primary" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">
                {editingCupom ? 'Editar Cupom' : 'Novo Cupom'}
              </h2>
            </div>

            <div className="space-y-4">
              {/* Código */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Código do Cupom *</label>
                <Input
                  data-testid="cupom-codigo-input"
                  value={cupomForm.codigo}
                  onChange={(e) => setCupomForm({ ...cupomForm, codigo: e.target.value.toUpperCase() })}
                  placeholder="Ex: BEMVINDO100"
                  className="rounded-xl font-mono uppercase"
                  maxLength={20}
                />
                <p className="text-xs text-gray-400 mt-1">Código que os usuários vão digitar</p>
              </div>

              {/* Desconto */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Percentual de Desconto *</label>
                <div className="relative">
                  <Input
                    data-testid="cupom-desconto-input"
                    type="number"
                    min="0"
                    max="100"
                    value={cupomForm.desconto_percent}
                    onChange={(e) => setCupomForm({ ...cupomForm, desconto_percent: Math.min(100, Math.max(0, parseInt(e.target.value) || 0)) })}
                    className="rounded-xl pr-10"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 font-medium">%</span>
                </div>
                {cupomForm.desconto_percent === 100 && (
                  <p className="text-xs text-green-600 mt-1 font-medium">
                    ✨ 100% = Upgrade automático para PRO
                  </p>
                )}
              </div>

              {/* Duração */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Duração (meses)
                  <span className="text-xs text-gray-500 ml-2">Vazio = permanente</span>
                </label>
                <Input
                  data-testid="cupom-duracao-input"
                  type="number"
                  min="1"
                  value={cupomForm.duracao_meses}
                  onChange={(e) => setCupomForm({ ...cupomForm, duracao_meses: e.target.value })}
                  placeholder="Ex: 12"
                  className="rounded-xl"
                  disabled={cupomForm.desconto_percent === 100}
                />
                {cupomForm.desconto_percent === 100 && (
                  <p className="text-xs text-amber-600 mt-1">
                    Cupons 100% são sempre permanentes
                  </p>
                )}
                {cupomForm.desconto_percent < 100 && cupomForm.duracao_meses && (
                  <p className="text-xs text-blue-600 mt-1">
                    Desconto válido por {cupomForm.duracao_meses} {parseInt(cupomForm.duracao_meses) === 1 ? 'mês' : 'meses'}
                  </p>
                )}
              </div>

              {/* Validade */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Início da Validade</label>
                  <Input
                    data-testid="cupom-inicio-input"
                    type="date"
                    value={cupomForm.validade_inicio}
                    onChange={(e) => setCupomForm({ ...cupomForm, validade_inicio: e.target.value })}
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Fim da Validade</label>
                  <Input
                    data-testid="cupom-fim-input"
                    type="date"
                    value={cupomForm.validade_fim}
                    onChange={(e) => setCupomForm({ ...cupomForm, validade_fim: e.target.value })}
                    className="rounded-xl"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-400 -mt-2">Deixe em branco para validade ilimitada</p>

              {/* Limite de usos */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Limite de Usos</label>
                <Input
                  data-testid="cupom-limite-input"
                  type="number"
                  min="0"
                  value={cupomForm.limite_usos}
                  onChange={(e) => setCupomForm({ ...cupomForm, limite_usos: e.target.value })}
                  placeholder="Ilimitado"
                  className="rounded-xl"
                />
                <p className="text-xs text-gray-400 mt-1">Deixe em branco para usos ilimitados</p>
              </div>

              {/* Descrição */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Descrição Interna</label>
                <Input
                  data-testid="cupom-descricao-input"
                  value={cupomForm.descricao}
                  onChange={(e) => setCupomForm({ ...cupomForm, descricao: e.target.value })}
                  placeholder="Ex: Cupom para evento X"
                  className="rounded-xl"
                />
                <p className="text-xs text-gray-400 mt-1">Apenas para controle interno, não visível aos usuários</p>
              </div>

              {/* Botões */}
              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  className="flex-1 rounded-xl"
                  onClick={() => setShowCupomModal(false)}
                >
                  Cancelar
                </Button>
                <Button
                  data-testid="cupom-save-button"
                  className="flex-1 rounded-xl bg-primary hover:bg-primary-hover text-white"
                  onClick={saveCupom}
                >
                  {editingCupom ? 'Salvar' : 'Criar Cupom'}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Serviço Create/Edit Modal */}
      {showServicoModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="max-w-md w-full rounded-2xl p-6 relative" data-testid="servico-modal">
            <button
              data-testid="close-servico-modal"
              onClick={() => setShowServicoModal(false)}
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
            >
              <X className="w-4 h-4 text-gray-600" />
            </button>

            <h2 className="text-xl font-bold text-gray-900 mb-6">
              {editingServico ? 'Editar Serviço' : 'Novo Serviço'}
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Profissão *</label>
                <select
                  data-testid="servico-profissao-select"
                  value={servicoForm.profissao_id}
                  onChange={(e) => setServicoForm({ ...servicoForm, profissao_id: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 rounded-xl border border-gray-200 focus:border-primary focus:ring-2 focus:ring-red-100 outline-none"
                >
                  <option value="">Selecione a profissão...</option>
                  {profissoes.map(prof => (
                    <option key={prof.id} value={prof.id}>{prof.nome}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Nome do Serviço *</label>
                <Input
                  data-testid="servico-nome-input"
                  value={servicoForm.nome}
                  onChange={(e) => setServicoForm({ ...servicoForm, nome: e.target.value })}
                  placeholder="Ex: Seguro Auto, Consulta, Revisão..."
                  className="rounded-xl"
                />
                <p className="text-xs text-gray-400 mt-1">Este nome aparecerá no dropdown para o usuário final</p>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  className="flex-1 rounded-xl"
                  onClick={() => setShowServicoModal(false)}
                >
                  Cancelar
                </Button>
                <Button
                  data-testid="servico-save-button"
                  className="flex-1 rounded-xl bg-primary hover:bg-primary-hover text-white"
                  onClick={saveServico}
                >
                  {editingServico ? 'Salvar' : 'Criar Serviço'}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Modal de Usos do Cupom */}
      {showUsosModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="max-w-2xl w-full rounded-2xl p-6 relative max-h-[80vh] overflow-hidden flex flex-col" data-testid="usos-modal">
            <button
              onClick={() => setShowUsosModal(false)}
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
            >
              <X className="w-4 h-4 text-gray-600" />
            </button>

            <div className="mb-6">
              <h2 className="text-xl font-bold text-gray-900">
                Usos do Cupom
              </h2>
              {usosData.cupom && (
                <div className="flex items-center gap-3 mt-2">
                  <code className="px-2 py-1 bg-gray-100 rounded text-sm font-mono font-bold text-gray-900">
                    {usosData.cupom.codigo}
                  </code>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                    usosData.cupom.desconto_percent === 100 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-blue-100 text-blue-800'
                  }`}>
                    {usosData.cupom.desconto_percent}% OFF
                  </span>
                  <span className="text-sm text-gray-500">
                    {usosData.cupom.usos_realizados} uso{usosData.cupom.usos_realizados !== 1 ? 's' : ''}
                  </span>
                </div>
              )}
            </div>

            <div className="flex-1 overflow-auto">
              {usosLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : usosData.usos.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Ticket className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>Nenhum uso registrado para este cupom.</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usuário</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usado em</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Desconto</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {usosData.usos.map((uso, index) => (
                      <tr key={uso.id || index} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900 text-sm">
                            {uso.nome_empresa || 'N/A'}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-gray-600">
                            {uso.email || 'N/A'}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-gray-600">
                            {uso.usado_em ? new Date(uso.usado_em).toLocaleString('pt-BR', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            }) : 'N/A'}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                            {uso.desconto_aplicado || usosData.cupom?.desconto_percent || 0}% OFF
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="mt-4 pt-4 border-t border-gray-200">
              <Button
                onClick={() => setShowUsosModal(false)}
                variant="outline"
                className="w-full rounded-xl"
              >
                Fechar
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Modal de Teste de Envio */}
      {showTesteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="max-w-md w-full rounded-2xl p-6 relative">
            <button
              onClick={() => setShowTesteModal(false)}
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
            >
              <X className="w-4 h-4 text-gray-600" />
            </button>

            <h2 className="text-xl font-bold text-gray-900 mb-2">Testar Envio de Notificação</h2>
            <p className="text-sm text-gray-500 mb-6">Envie um email e/ou WhatsApp de teste para verificar se está funcionando.</p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Email (opcional)</label>
                <Input
                  type="email"
                  value={testeForm.email}
                  onChange={(e) => setTesteForm({ ...testeForm, email: e.target.value })}
                  placeholder="email@exemplo.com"
                  className="rounded-xl"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Telefone WhatsApp (opcional)</label>
                <Input
                  value={testeForm.telefone}
                  onChange={(e) => setTesteForm({ ...testeForm, telefone: e.target.value.replace(/\D/g, '') })}
                  placeholder="21999999999 (DDD + número)"
                  className="rounded-xl"
                />
                <p className="text-xs text-gray-400 mt-1">Apenas números, sem o 55</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Nome do Cliente</label>
                <Input
                  value={testeForm.cliente_nome}
                  onChange={(e) => setTesteForm({ ...testeForm, cliente_nome: e.target.value })}
                  placeholder="Nome para teste"
                  className="rounded-xl"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Tipo de Serviço</label>
                <Input
                  value={testeForm.tipo_servico}
                  onChange={(e) => setTesteForm({ ...testeForm, tipo_servico: e.target.value })}
                  placeholder="Ex: Seguro Auto"
                  className="rounded-xl"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Dias até vencimento</label>
                <select
                  value={testeForm.dias_restantes}
                  onChange={(e) => setTesteForm({ ...testeForm, dias_restantes: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 rounded-xl border border-gray-200 focus:border-primary focus:ring-2 focus:ring-red-100 outline-none"
                >
                  <option value={0}>Vence hoje (D-0)</option>
                  <option value={7}>Vence em 7 dias (D-7)</option>
                  <option value={15}>Vence em 15 dias (D-15)</option>
                </select>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  className="flex-1 rounded-xl"
                  onClick={() => setShowTesteModal(false)}
                >
                  Cancelar
                </Button>
                <Button
                  className="flex-1 rounded-xl bg-primary hover:bg-primary-hover text-white"
                  onClick={enviarTeste}
                  disabled={testeEnviando || (!testeForm.email && !testeForm.telefone)}
                >
                  {testeEnviando ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Enviar Teste
                    </>
                  )}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminBackoffice;
